import {
  LazyLoadEventHover,
  LazyLoader,
  loadImages,
} from "@NextSkyTheme/lazy-load";
import { CustomElement } from "@NextSkyTheme/safari-element-patch";
import * as NextSkyTheme from "@NextSkyTheme/global";

let slideModulePromise = null;
let variantSwatchModulePromise = null;
let addToCartModulePromise = null;
let notificationModulePromise = null;
let modalModulePromise = null;
let customerAddressesModulePromise = null;
let cartExtrasModulePromise = null;

function loadNotificationModule() {
  if (!notificationModulePromise) {
    notificationModulePromise = import("@NextSkyTheme/notification");
    notificationModulePromise.catch(() => {
      notificationModulePromise = null;
    });
  }
  return notificationModulePromise;
}

function loadModalModule() {
  if (!modalModulePromise) {
    modalModulePromise = import("@NextSkyTheme/modal");
    modalModulePromise.catch(() => {
      modalModulePromise = null;
    });
  }
  return modalModulePromise;
}

const notifier = {
  show(...args) {
    return loadNotificationModule().then((module) =>
      module.notifier.show(...args),
    );
  },
  showElement(...args) {
    return loadNotificationModule().then((module) =>
      module.notifier.showElement(...args),
    );
  },
};

function eventModal(...args) {
  return loadModalModule().then((module) => module.eventModal(...args));
}

function loadSlideModule() {
  if (!slideModulePromise) {
    slideModulePromise = import("@NextSkyTheme/slide");
    slideModulePromise.catch(() => {
      slideModulePromise = null;
    });
  }
  return slideModulePromise;
}

async function initSlideOnDemand(element) {
  const { initSlide } = await loadSlideModule();
  return initSlide(element);
}

function loadVariantSwatchModule(root = document) {
  if (!root?.querySelector?.("variant-swatch-select")) {
    return Promise.resolve(null);
  }

  if (!variantSwatchModulePromise) {
    variantSwatchModulePromise = import("@NextSkyTheme/variant-swatch");
    variantSwatchModulePromise.catch(() => {
      variantSwatchModulePromise = null;
    });
  }

  return variantSwatchModulePromise;
}

function hasAddToCartSurface(root = document) {
  return Boolean(
    root?.querySelector?.(
      [
        "product-form",
        'form[action*="/cart/add"]',
        "cart-drawer",
        "main-cart",
        "cart-remove-button",
        "main-cart-remove-button",
        "cart-items",
        "main-cart-items",
      ].join(","),
    ),
  );
}

function loadAddToCartModule() {
  if (!addToCartModulePromise) {
    addToCartModulePromise = import("@NextSkyTheme/add-to-cart");
    addToCartModulePromise.catch(() => {
      addToCartModulePromise = null;
    });
  }
  return addToCartModulePromise;
}

function loadCustomerAddressesModule() {
  if (!customerAddressesModulePromise) {
    customerAddressesModulePromise = import("@NextSkyTheme/customer-addresses");
    customerAddressesModulePromise.catch(() => {
      customerAddressesModulePromise = null;
    });
  }
  return customerAddressesModulePromise;
}

function loadCartExtrasModule() {
  if (!cartExtrasModulePromise) {
    cartExtrasModulePromise = import("@NextSkyTheme/cart-extras");
    cartExtrasModulePromise.catch(() => {
      cartExtrasModulePromise = null;
    });
  }
  return cartExtrasModulePromise;
}

function setupCustomerAddressesLoader(root = document) {
  if (
    root?.querySelector?.(
      ".bls-customer__address, .add-address, .address-delete, .edit-country-option",
    )
  ) {
    loadCustomerAddressesModule();
  }
}

function hasCartExtrasSurface(root = document) {
  return Boolean(
    root?.querySelector?.(
      [
        "main-cart",
        "cart-estimate-element",
        "cart-note-element",
        "main-cart-note",
        "mini-cart-recommendations",
        ".drawer__cart-shipping",
        ".drawer__cart-note",
        ".drawer__cart-recommendations",
        ".cart-recommendations",
      ].join(","),
    ),
  );
}

function loadCartExtrasForCartPage(root = document) {
  if (document.body?.classList?.contains("template-cart") || root?.querySelector?.("main-cart")) {
    loadCartExtrasModule();
  }
}

function setupCartExtrasInteractionLoader() {
  const cartExtrasTriggerSelector = [
    "#cart-icon-bubble",
    "#cart-icon-bubble-mobile",
    ".drawer__cart-shipping",
    ".drawer__cart-note",
    ".toggle-addons",
  ].join(",");

  document.addEventListener(
    "click",
    (event) => {
      if (customElements.get("cart-estimate-element") && customElements.get("cart-note-element")) return;
      if (!event.target?.closest?.(cartExtrasTriggerSelector)) return;
      loadCartExtrasModule();
    },
    true,
  );

  document.addEventListener("nextsky:cart:added", () => {
    if (hasCartExtrasSurface()) loadCartExtrasModule();
  });
}

function setupAddToCartLoader() {
  const cartTriggerSelector = "#cart-icon-bubble, #cart-icon-bubble-mobile";

  document.addEventListener(
    "submit",
    (event) => {
      if (customElements.get("product-form")) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
      loadAddToCartModule().then(() => {
        loadCartExtrasModule();
        if (!form.isConnected) return;
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit(submitter);
        } else {
          form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
        }
      });
    },
    true,
  );

  document.addEventListener(
    "click",
    (event) => {
      if (customElements.get("cart-drawer")) return;
      const trigger = event.target?.closest?.(cartTriggerSelector);
      if (!trigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      loadAddToCartModule().then(() => {
        loadCartExtrasModule();
        if (trigger.isConnected) trigger.click();
      });
    },
    true,
  );

  const scheduleFallback = () => {
    window.setTimeout(() => {
      if (hasAddToCartSurface()) loadAddToCartModule();
      loadCartExtrasForCartPage();
    }, 7000);
  };

  if (document.readyState === "complete") {
    scheduleFallback();
  } else {
    window.addEventListener("load", scheduleFallback, { once: true });
  }
}

LazyLoadEventHover.run();
new LazyLoader(".image-lazy-load");
new NextSkyTheme.FSProgressBar("free-ship-progress-bar");
setupAddToCartLoader();
setupCartExtrasInteractionLoader();
loadCartExtrasForCartPage();
loadVariantSwatchModule();
setupCustomerAddressesLoader();

document.addEventListener("shopify:section:load", function () {
  new LazyLoader(".image-lazy-load");
  new NextSkyTheme.FSProgressBar("free-ship-progress-bar");
  loadCartExtrasForCartPage(document);
  loadVariantSwatchModule(document);
  setupCustomerAddressesLoader(document);
});

function safeParseJSON(raw, fallback = null) {
  try {
    if (!raw) return fallback;
    var parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch (error) {
    return fallback;
  }
}

function safeSetSessionStorage(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Unable to write ${key} to sessionStorage.`, error);
  }
}

function safeRemoveSessionStorage(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn(`Unable to remove ${key} from sessionStorage.`, error);
  }
}

try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function eventFlashingBrowseTab() {
  const { enable, firstNotification, secondaryNotification } =
    window.flashingBrowseTab || {};
  const titleTag = document.querySelector("title");
  if (!enable || !titleTag) return;
  const originalTitle = titleTag.innerText;
  let isActive = true;
  let myTimer = null;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      if (!isActive) {
        clearInterval(myTimer);
        titleTag.innerText = originalTitle;
        isActive = true;
      }
    } else {
      if (!firstNotification || !secondaryNotification) return;
      isActive = false;
      let toggle = true;
      myTimer = setInterval(() => {
        titleTag.innerText = toggle ? firstNotification : secondaryNotification;
        toggle = !toggle;
      }, 2000);
    }
  });
}
eventFlashingBrowseTab();

function eventScrollbarWidth() {
  requestAnimationFrame(() => {
    NextSkyTheme.getRoot().style.setProperty(
      "--scrollbar-width",
      NextSkyTheme.getScrollbarWidth() + "px",
    );
    window.addEventListener("resize", () => {
      NextSkyTheme.getRoot().style.setProperty(
        "--scrollbar-width",
        NextSkyTheme.getScrollbarWidth() + "px",
      );
    });
  });
}

function supportsStableScrollbarGutter() {
  return (
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("scrollbar-gutter: stable")
  );
}

function initializeScrollbarWidth(fallback = eventScrollbarWidth) {
  if (supportsStableScrollbarGutter()) {
    NextSkyTheme.getRoot().style.setProperty("--scrollbar-width", "0px");
    return;
  }

  fallback();
}

initializeScrollbarWidth(eventScrollbarWidth);

function setupScrollRestoration() {
  const state = window.__nextSkyScrollRestore;
  if (!state) return;

  const saveScrollPosition = () => {
    const scrollY = Math.max(window.scrollY || window.pageYOffset || 0, 0);

    if (scrollY > 0) {
      safeSetSessionStorage(state.storageKey, String(Math.round(scrollY)));
    } else {
      safeRemoveSessionStorage(state.storageKey);
    }
  };

  window.addEventListener("pagehide", saveScrollPosition, { capture: true });
  window.addEventListener("beforeunload", saveScrollPosition, {
    capture: true,
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      saveScrollPosition();
    }
  });

  if (!state.shouldRestore) return;

  let frameCount = 0;
  const maxFrames = 90;
  let restoreStarted = false;

  const release = () => {
    window.scrollTo(0, state.savedScrollY);
    document.documentElement.classList.remove("is-restoring-scroll");
    history.scrollRestoration = "auto";
  };

  const restoreScroll = () => {
    frameCount += 1;
    window.scrollTo(0, state.savedScrollY);

    const currentScrollY = window.scrollY || window.pageYOffset || 0;
    const currentMaxScroll = Math.max(
      document.documentElement.scrollHeight - window.innerHeight,
      0,
    );
    const targetScrollY = Math.min(state.savedScrollY, currentMaxScroll);
    const isRestored = Math.abs(currentScrollY - targetScrollY) <= 2;

    if (isRestored || frameCount >= maxFrames) {
      requestAnimationFrame(() => {
        requestAnimationFrame(release);
      });
      return;
    }

    requestAnimationFrame(restoreScroll);
  };

  const startRestore = () => {
    if (restoreStarted) return;
    restoreStarted = true;
    requestAnimationFrame(restoreScroll);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startRestore, { once: true });
  } else {
    startRestore();
  }

  window.addEventListener("pageshow", startRestore, { once: true });
}

setupScrollRestoration();

function setupSectionTextReveal(root = document) {
  const selectors = [
    ".bbr-text-reveal",
    ".section-header :is(.section-subheading, .section-heading, .section-description, .section-sub-desc)",
    ".section-ugc-reviews :is(.section-subheading, .section-heading, .section-sub-desc)",
    ".assur-head :is(.eyebrow, .h2)",
    ".blog-head :is(.eyebrow, .h2)",
    ".signup-left :is(.eyebrow, .h2, .lead)",
    ".pov-copy :is(.pov-eyebrow, .pov-brand, .pov-heading, .pov-subtitle)",
    ".pov-glass :is(.pov-glass-kicker, .pov-glass-title)",
    ".full-text :is(.tag, .headline, .desc)",
    ".mc-content :is(.mc-heading, .mc-sub)",
    ".mc-header :is(.mc-label, .mc-title)",
    ".mc-caption :is(.mc-caption-title, .mc-caption-desc)",
    ".fg-header :is(.fg-label, .fg-title)",
    ".fg-caption :is(.fg-caption-title, .fg-caption-desc)",
    ".fh-header :is(.fh-kicker, .fh-title, .fh-subtitle)",
    ".fh-copy :is(.fh-copy-kicker, .fh-copy-title, .fh-body)",
    ".pc-header :is(.pc-eyebrow, .pc-title, .pc-desc)",
    ".faq-header :is(.faq-label, .faq-title, .faq-subtitle)",
    ".cr-section-header :is(.cr-eyebrow, .cr-title, .cr-subtitle)",
    ".bst-left :is(.bst-eyebrow, .bst-title, .bst-subtitle)",
    ".c-text :is(.c-eyebrow, h1 > span, .c-joined-text)",
    ".tl-header :is(.tl-eyebrow, .tl-title)",
    ".ba-header :is(.ba-eyebrow, .ba-title)",
    ".bp-header :is(.bp-eyebrow, .bp-title, .bp-subtitle)",
    ".guide-head :is(.eyebrow, .h2, .lead)",
    ".sc-head :is(.sc-eyebrow, .sc-title, .sc-desc)",
    ".hw-header :is(.eyebrow, .hw-title, .lead)",
    ".wd-header :is(.eyebrow, .wd-title, .lead)",
    ".pg-header .pg-title",
    ".wg-header .wg-title",
    ".bm-eyebrow",
    ".bm-heading",
    ".be-quote-col :is(.be-quote-text, .be-quote-attrib)",
    ".be-body-col :is(.be-eyebrow, .be-body-text)",
    ".b2-heading",
    ".footer__block-heading",
    ".footer-block__newsletter-heading",
    ".footer__block-description",
  ].join(",");

  const scope = root instanceof Element ? root : document;
  const elements = Array.from(scope.querySelectorAll(selectors)).filter((el) => {
    if (el.dataset.bbrTextReveal === "true") return false;
    if (!el.textContent || !el.textContent.trim()) return false;
    if (el.closest("[data-no-text-reveal], .no-text-reveal")) return false;
    if (el.closest(".blog-card, .review-card, .ugc-card, .assur-item")) return false;
    return true;
  });

  if (!elements.length) return;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  elements.forEach((el, index) => {
    el.dataset.bbrTextReveal = "true";
    el.classList.add("bbr-text-reveal");
    el.style.setProperty("--bbr-text-reveal-delay", `${Math.min(index * 60, 180)}ms`);
    if (reduceMotion) {
      el.classList.add("is-visible");
    }
  });

  if (reduceMotion) return;

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        obs.unobserve(entry.target);
      });
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.12 },
  );

  elements.forEach((el) => observer.observe(el));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => setupSectionTextReveal(), {
    once: true,
  });
} else {
  setupSectionTextReveal();
}

document.addEventListener("shopify:section:load", (event) => {
  setupSectionTextReveal(event.target);
});

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", () => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

const Shopify = (window.Shopify = window.Shopify || {});

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.textContent) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid,
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this),
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function () {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = safeParseJSON(raw, []);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.textContent = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.textContent = values[i];
      selector.appendChild(opt);
    }
  },
};

class PageLoader extends HTMLElement {
  constructor() {
    super();
    this.boundLoad = this.onLoad.bind(this);
    this.bindEvents();
  }

  bindEvents() {
    window.addEventListener("load", this.boundLoad, false);
  }

  onLoad() {
    const loadingContainer = this.querySelector(".loading-container");
    if (loadingContainer) {
      Motion.animate(
        loadingContainer,
        { scale: [1, 0.5], opacity: [1, 0] },
        { duration: 0.3 },
      );
    }
    Motion.animate(this, { opacity: [1, 0] }, { duration: 0.3 }).finished.then(
      () => {
        this.remove();
      },
    );
  }

  disconnectedCallback() {
    window.removeEventListener("load", this.boundLoad);
  }
}
customElements.define("page-loader", PageLoader);

class BackToTop extends HTMLElement {
  constructor() {
    super();
    this.boundBackToTop = this.backToTop.bind(this);
    this.boundKeyup = this.handleKeyup.bind(this);
    this.scrollFrame = null;
    this.addEventListener("click", this.boundBackToTop, false);
    this.addEventListener("keyup", this.boundKeyup);
    this.throttledScroll = NextSkyTheme.throttle(
      this.updateScrollPercentage.bind(this),
      16,
    );
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    this.easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  connectedCallback() {
    window.addEventListener("scroll", this.throttledScroll);
  }

  handleKeyup(event) {
    if (event.code.toUpperCase() === "ENTER") {
      this.backToTop();
    }
  }

  backToTop() {
    if (document.documentElement.scrollTop > 0 || document.body.scrollTop > 0) {
      this.smoothScrollTo(0, 600, 0.1);
      this.blur();
    }
  }

  updateScrollPercentage() {
    const scrollHeight =
      document.documentElement.scrollHeight || document.body.scrollHeight;
    const scrollTop =
      document.documentElement.scrollTop || document.body.scrollTop;
    const clientHeight =
      document.documentElement.clientHeight || document.body.clientHeight;
    const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
    this.style.setProperty(
      "--scroll-percentage",
      scrollPercentage.toFixed(2) + "%",
    );
    if (scrollTop > 200) {
      this.classList.add("show");
    } else {
      this.classList.remove("show");
    }
  }

  smoothScrollTo(y, baseDuration = 650, kick = 0.2) {
    if (this.prefersReducedMotion) {
      window.scrollTo(0, y);
      return;
    }
    const startY = window.pageYOffset;
    const delta = y - startY;
    const dist = Math.abs(delta);

    const d = Math.max(620, Math.min(1200, baseDuration * (dist / 1400 + 1)));
    const start = performance.now() - d * kick;

    const step = (now) => {
      const t = Math.min(1, (now - start) / d);
      const eased = this.easeInOutCubic(t);
      window.scrollTo(0, startY + delta * eased);
      if (t < 1 && this.isConnected) {
        this.scrollFrame = requestAnimationFrame(step);
      } else {
        this.scrollFrame = null;
      }
    };
    this.scrollFrame = requestAnimationFrame(step);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.throttledScroll);
    this.removeEventListener("click", this.boundBackToTop);
    this.removeEventListener("keyup", this.boundKeyup);
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }
}
customElements.define("back-to-top", BackToTop);

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  get dataStickyType() {
    return this.hasAttribute("data-sticky-type")
      ? this.getAttribute("data-sticky-type")
      : "none";
  }
  get heightAnnouncementBar() {
    // Cache the announcement bar element
    if (!this._announcementBar) {
      this._announcementBar = document.querySelector(
        ".section-announcement-bar",
      );
    }
    return this._announcementBar
      ? Math.round(this._announcementBar.clientHeight)
      : 0;
  }

  init() {
    this.check = 0;
    this.onStickyHeader();
  }

  onStickyHeader() {
    if (this.dataStickyType != "none") {
      if (this.dataStickyType === "on-scroll-up") {
        this.closest(".site-header").classList.add("scroll-up");
      }
      if (this.dataStickyType === "always") {
        this.closest(".site-header").classList.add("header-sticky");
      }
      // Throttle scroll event for better performance
      this.throttledStickyFunction = NextSkyTheme.throttle(
        this.stickyFunction.bind(this),
        16,
      );
      window.addEventListener("scroll", this.throttledStickyFunction, {
        passive: true,
      });
    }
  }

  stickyFunction() {
    let headerHeight =
      this.heightAnnouncementBar + Math.round(this.clientHeight);
    let header = this.closest(".site-header");
    let positionScrollY = window.scrollY;
    const isScrolling = this._lastScrollY !== positionScrollY;
    this._lastScrollY = positionScrollY;

    if (header && isScrolling) {
      if (this.dataStickyType === "always") {
        if (positionScrollY > headerHeight) {
          header.classList.add("section-header-sticky", "animate");
        } else {
          header.classList.remove("section-header-sticky", "animate");
        }
      } else {
        if (positionScrollY > 0) {
          if (positionScrollY > headerHeight) {
            header.classList.add("scr-pass-header");
            if (positionScrollY > this.check) {
              header.classList.add("header-sticky-hidden");
            } else {
              header.classList.remove("header-sticky-hidden");
              header.classList.add("animate");
            }
            header.classList.add("section-header-sticky");
            this.check = positionScrollY;
          } else {
            header.classList.remove("scr-pass-header");
            if (positionScrollY < 10 + this.heightAnnouncementBar) {
              header.classList.remove(
                "header-sticky-hidden",
                "section-header-sticky",
                "animate",
              );
            }
            this.check = 0;
          }
        } else {
          header.classList.remove(
            "header-sticky-hidden",
            "section-header-sticky",
            "animate",
          );
        }
      }
    }
  }
  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    if (this.throttledStickyFunction) {
      window.removeEventListener("scroll", this.throttledStickyFunction);
    }
    // Clear cached elements
    this._announcementBar = null;
  }
}
customElements.define("site-header", SiteHeader, {
  extends: "header",
});
CustomElement.observeAndPatchCustomElements({
  "site-header": {
    tagElement: "header",
    classElement: SiteHeader,
  },
});

class LazyLoadingImg extends HTMLImageElement {
  constructor() {
    super();
    this.boundImageLoad = this.onImageLoad.bind(this);
    this.boundImageError = this.onImageError.bind(this);
    this.mutationObserver = null;
    this.init();
  }

  init() {
    // Cache media element to avoid repeated DOM queries
    this._media =
      this.closest(".image__media") || this.closest(".image-picture");

    if (this._media) {
      // Throttle resize event for better performance
      this.throttledLazyLoading = NextSkyTheme.throttle(
        this.lazyLoading.bind(this),
        100,
      );
      window.addEventListener("resize", this.throttledLazyLoading, {
        passive: true,
      });

      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.attributeName.includes("src") ||
            mutation.attributeName.includes("srcset")
          ) {
            this.lazyLoading();
          }
        });
      });

      this.mutationObserver.observe(this, { attributes: true });
      this.lazyLoading();
    }
  }

  get media() {
    return this._media;
  }

  get elementParent() {
    return this.closest("motion-effect");
  }

  lazyLoading() {
    if (this.classList.contains("loaded")) return;
    this.removeEventListener("load", this.boundImageLoad);
    this.removeEventListener("error", this.boundImageError);
    this.addEventListener("load", this.boundImageLoad);
    this.addEventListener("error", this.boundImageError);
    if (this.complete) {
      this.onImageLoad();
      return;
    }
    this.media?.classList.add("loading_img");
  }

  onImageLoad() {
    this.finishLoading();
  }

  onImageError() {
    this.classList.add("load-error");
    this.finishLoading();
  }

  finishLoading() {
    this.classList.add("loaded");
    this.media?.classList.remove("loading_img");
    if (this.elementParent) {
      this.elementParent.classList.remove("loading");
      this.elementParent.classList.add("loaded");
    }
    this.removeEventListener("load", this.boundImageLoad);
    this.removeEventListener("error", this.boundImageError);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.throttledLazyLoading) {
      window.removeEventListener("resize", this.throttledLazyLoading);
    }
    this.removeEventListener("load", this.boundImageLoad);
    this.removeEventListener("error", this.boundImageError);
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    // Clear cached elements
    this._media = null;
  }
}
customElements.define("lazy-loading-img", LazyLoadingImg, { extends: "img" });
CustomElement.observeAndPatchCustomElements({
  "lazy-loading-img": {
    tagElement: "img",
    classElement: LazyLoadingImg,
  },
});

class ToggleMenu extends HTMLElement {
  constructor() {
    super();
    this.container = this.closest(".header__inner");
    this.boundClick = this.onClick.bind(this);
    this.openTimer = null;
    this.currentDrawer = null;
    this.init();
  }
  init() {
    this.addEventListener("click", this.boundClick, false);
  }
  onClick(e) {
    e.preventDefault();
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }

    this.removePendingDrawer();
    let drawer = null;
    const menu_drawer = this.closest(".header-wrapper").querySelector(
      "template.menu-drawer",
    );
    if (menu_drawer) {
      const content = document.createElement("div");
      content.appendChild(
        menu_drawer.content.firstElementChild.cloneNode(true),
      );
      drawer = content.querySelector("menu-drawer");
      if (drawer) {
        NextSkyTheme.getBody().appendChild(drawer);
        this.currentDrawer = drawer;
      }
    }
    if (drawer) {
      this.openTimer = setTimeout(() => {
        this.openTimer = null;
        if (this.isConnected && drawer.isConnected) {
          NextSkyTheme.global.rootToFocus = this.querySelector("button") || this;
          eventModal(drawer, "open", true);
        }
      }, 100);
    }
    new LazyLoader(".image-lazy-load");
    CustomElement.observeAndPatchCustomElements({
      "button-close-model": {
        tagElement: "button",
        classElement: ButtonCloseModel,
      },
      "details-mega-menu": {
        tagElement: "details",
        classElement: DetailsMegaMenu,
      },
      "submenu-details": {
        tagElement: "details",
        classElement: SubMenuDetails,
      },
    });
  }

  removePendingDrawer() {
    if (!this.currentDrawer || this.currentDrawer.classList.contains("active")) {
      return;
    }

    this.currentDrawer.remove();
    this.currentDrawer = null;
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundClick);
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.removePendingDrawer();
    this.currentDrawer = null;
  }
}
customElements.define("toggle-menu", ToggleMenu);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.width = window.innerWidth;
    this.boundResize = this.handleResize.bind(this);
    this.init();
  }
  init() {
    window.addEventListener("resize", this.boundResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.boundResize);
  }

  handleResize() {
    const overlay = this.querySelector(".modal-overlay");
    const newWidth = window.innerWidth;
    if (overlay && newWidth > 1024 && this.width <= 1024) {
      eventModal(overlay, "close");
    }
    this.width = newWidth;
  }
}
customElements.define("menu-drawer", MenuDrawer);

class ModalOverlay extends HTMLElement {
  constructor() {
    super();
    this.boundClick = this.onClick.bind(this);
    this.boundKeyup = this.handleKeyup.bind(this);
    this.overlayParent = null;
    this.init();
  }
  init() {
    this.overlayParent = this.parentElement;
    this.overlayParent?.addEventListener("keyup", this.boundKeyup);
    this.addEventListener("click", this.boundClick, false);
  }

  disconnectedCallback() {
    this.overlayParent?.removeEventListener("keyup", this.boundKeyup);
    this.removeEventListener("click", this.boundClick);
    this.overlayParent = null;
  }

  handleKeyup(event) {
    if (event?.code?.toUpperCase() === "ESCAPE") {
      this.onClick();
    }
  }

  onClick() {
    eventModal(this, "close");
  }
}
customElements.define("modal-overlay", ModalOverlay);

class ButtonCloseModel extends HTMLButtonElement {
  constructor() {
    super();
    this.boundClick = this.onClick.bind(this);
    this.init();
  }
  init() {
    this.addEventListener("click", this.boundClick, false);
  }
  onClick(e) {
    eventModal(this, "close");
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundClick);
  }
}
customElements.define("button-close-model", ButtonCloseModel, {
  extends: "button",
});
CustomElement.observeAndPatchCustomElements({
  "button-close-model": {
    tagElement: "button",
    classElement: ButtonCloseModel,
  },
});

class LoadingButton extends HTMLButtonElement {
  constructor() {
    super();
    this.boundClick = this.onClick.bind(this);
    this.loadingTimer = null;
    this.init();
  }
  init() {
    this.addEventListener("click", this.boundClick, false);
  }
  onClick(e) {
    this.classList.add("loading");
    this.loadingTimer = setTimeout(() => {
      this.loadingTimer = null;
      if (this.isConnected) {
        this.classList.remove("loading");
      }
    }, 1500);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundClick);
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
  }
}
customElements.define("loading-button", LoadingButton, {
  extends: "button",
});
CustomElement.observeAndPatchCustomElements({
  "loading-button": {
    tagElement: "button",
    classElement: LoadingButton,
  },
});

const megaMenuCount = new WeakMap();
class DetailsMegaMenu extends HTMLDetailsElement {
  constructor() {
    (super(),
      (this.summaryElement = false),
      (this.contentElement = false),
      (this._open = false),
      (this.header = false),
      (this.timeoutIds = []),
      (this.boundSummaryClicked = this.onSummaryClicked.bind(this)),
      (this.boundBlockSelect = this.handleBlockSelect.bind(this)),
      (this.boundBlockDeselect = this.handleBlockDeselect.bind(this)),
      this.init());
  }

  set open(value) {
    value !== this._open &&
      ((this._open = value),
      this.isConnected
        ? this.transition(value)
        : value
          ? this.setAttribute("open", "")
          : this.removeAttribute("open"));
  }

  get open() {
    return this._open;
  }

  get dropdownsAnimation() {
    return this.header?.hasAttribute("data-dropdowns-animation")
      ? this.header.getAttribute("data-dropdowns-animation")
      : "fade-in";
  }

  get menuTrigger() {
    return this.header?.hasAttribute("data-menu-trigger") &&
      window.innerWidth >= 1025
      ? this.header.getAttribute("data-menu-trigger")
      : "click";
  }

  init() {
    ((this.summaryElement = this.firstElementChild),
      (this.contentElement = this.lastElementChild),
      (this._open = this.hasAttribute("open")),
      (this.header = document.querySelector("header")),
      this.summaryElement &&
      this.summaryElement.addEventListener(
        "click",
        this.boundSummaryClicked,
      ),
      (this.detectClickOutsideListener = this.detectClickOutside.bind(this)),
      (this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this)),
      (this.detectFocusOutListener = this.detectFocusOut.bind(this)),
      (this.detectHoverListener = this.detectHover.bind(this)),
      this.addEventListener("mouseenter", this.detectHoverListener),
      this.addEventListener("mouseleave", this.detectHoverListener));
    if (this.querySelector(".back-menu")) {
      this.querySelector(".back-menu").addEventListener(
        "click",
        this.boundSummaryClicked,
      );
    }
    window.Shopify.designMode &&
      (this.addEventListener("shopify:block:select", this.boundBlockSelect),
      this.addEventListener("shopify:block:deselect", this.boundBlockDeselect));
  }

  disconnectedCallback() {
    this.summaryElement?.removeEventListener("click", this.boundSummaryClicked);
    this.querySelector(".back-menu")?.removeEventListener(
      "click",
      this.boundSummaryClicked,
    );
    this.removeEventListener("mouseenter", this.detectHoverListener);
    this.removeEventListener("mouseleave", this.detectHoverListener);
    this.removeEventListener("shopify:block:select", this.boundBlockSelect);
    this.removeEventListener("shopify:block:deselect", this.boundBlockDeselect);
    document.removeEventListener("click", this.detectClickOutsideListener);
    document.removeEventListener("keydown", this.detectEscKeyboardListener);
    document.removeEventListener("focusout", this.detectFocusOutListener);
    clearTimeout(this._hoverTimer);
    this._hoverTimer = null;
    this.timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeoutIds = [];
  }

  setManagedTimeout(callback, delay) {
    const timeoutId = setTimeout(() => {
      this.timeoutIds = this.timeoutIds.filter((id) => id !== timeoutId);
      if (this.isConnected) callback();
    }, delay);
    this.timeoutIds.push(timeoutId);
  }

  handleBlockSelect() {
    if (window.innerWidth >= 1025) {
      this.open = !0;
      return;
    }
    const menu = document.querySelector("menu-drawer");
    if (menu && !document.body.classList.contains("selected-menu")) {
      document.body.classList.add("selected-menu");
    }
    const menu_drawer = this.closest(".header-wrapper")?.querySelector(
      "template.menu-drawer",
    );
    if (menu_drawer && !menu) {
      const content = document.createElement("div");
      content.appendChild(
        menu_drawer.content.firstElementChild.cloneNode(true),
      );
      NextSkyTheme.getBody().appendChild(
        content.querySelector("menu-drawer"),
      );
    }
    this.setManagedTimeout(() => {
      if (!menu) {
        const drawer = document.querySelector("menu-drawer");
        if (drawer) eventModal(drawer, "open", true);
      }
      document.body.classList.remove("selected-menu");
    }, 100);
    new LazyLoader(".image-lazy-load");
  }

  handleBlockDeselect() {
    if (window.innerWidth >= 1025) {
      this.open = !1;
      return;
    }
    this.setManagedTimeout(() => {
      const menu_drawer = document.querySelector("menu-drawer");
      if (
        menu_drawer &&
        !document.body.classList.contains("selected-menu")
      ) {
        eventModal(menu_drawer, "close", false);
      }
    }, 100);
  }

  onSummaryClicked(event) {
    (event.preventDefault(),
      this.menuTrigger === "hover" &&
      this.summaryElement.hasAttribute("data-href") &&
      this.summaryElement.getAttribute("data-href").length > 0 &&
      (event.pointerType || this._open === true)
        ? (window.location.href = this.summaryElement.getAttribute("data-href"))
        : (this.open = !this.open));
  }
  async transition(value) {
    if (value) {
      megaMenuCount.set(
        DetailsMegaMenu,
        megaMenuCount.get(DetailsMegaMenu) + 1,
      );
      this.setAttribute("open", "");
      this.summaryElement.setAttribute("open", "");
      document.addEventListener("click", this.detectClickOutsideListener);
      document.addEventListener("keydown", this.detectEscKeyboardListener);
      document.addEventListener("focusout", this.detectFocusOutListener);
      this.classList.add("detail-open");
      if (window.innerWidth >= 1025) {
        NextSkyTheme.getBody().classList.add("dropdown-open");
      }
      if (
        this.dropdownsAnimation === "fade-in-down" &&
        window.innerWidth >= 1025
      ) {
        this.contentElement.setAttribute("open", "");
        this.initDeferredSlides();
        this.contentElement.classList.add("expanding");
        await this.fadeInDown();
        this.contentElement.classList.remove("expanding");
      } else {
        if (window.innerWidth >= 1025) {
          await this.fadeInDown();
        }
        this.setManagedTimeout(
          () => {
            this.contentElement?.setAttribute("open", "");
            this.initDeferredSlides();
          },
          100,
        );
      }
    } else {
      megaMenuCount.set(
        DetailsMegaMenu,
        megaMenuCount.get(DetailsMegaMenu) - 1,
      );
      this.summaryElement.removeAttribute("open");
      this.contentElement.removeAttribute("open");
      document.removeEventListener("click", this.detectClickOutsideListener);
      document.removeEventListener("keydown", this.detectEscKeyboardListener);
      document.removeEventListener("focusout", this.detectFocusOutListener);
      this.classList.remove("detail-open");
      NextSkyTheme.getBody().classList.remove("dropdown-open");
      if (
        this.dropdownsAnimation === "fade-in-down" &&
        window.innerWidth >= 1025
      ) {
        this.contentElement.classList.add("expanding");
        await this.fadeInUp();
        this.contentElement.classList.remove("expanding");
        if (!this.open) this.removeAttribute("open");
      } else {
        this.setManagedTimeout(() => {
          if (!this.open) this.removeAttribute("open");
        }, 300);
        if (window.innerWidth >= 1025) {
          this.fadeInUp();
        }
      }
    }
  }
  initDeferredSlides() {
    this.contentElement
      ?.querySelectorAll('slide-section[data-lazy-init="true"]')
      .forEach((slide) => {
        if (typeof slide.init === "function") {
          slide.init();
        }
      });
  }
  detectClickOutside(event) {
    !this.contains(event.target) && (this.open = !1);
  }
  detectEscKeyboard(event) {
    if (event.code === "Escape") {
      const targetMenu = event.target.closest("details[open]");
      targetMenu &&
        ((targetMenu.open = !1),
        targetMenu.firstElementChild.focus({ focusVisible: true }));
    }
  }
  detectFocusOut(event) {
    event.relatedTarget &&
      window.innerWidth >= 1025 &&
      !this.contains(event.relatedTarget) &&
      (this.open = !1);
  }
  detectHover(event) {
    this.menuTrigger !== "hover" ||
      (event.type === "mouseenter"
        ? (clearTimeout(this._hoverTimer),
          (this._delayEligible = !!event.target
            .closest?.("li")
            ?.querySelector?.(".submenu")),
          !this._delayEligible
            ? ((this.open = true),
              (this._hoverTimer = null),
              (this._hoverDelay = false),
              (this._skipHoverDelay = false))
            : this._skipHoverDelay
              ? ((this.open = true), (this._hoverTimer = null))
              : (this._hoverTimer = setTimeout(() => {
                  this.open = true;
                  this._hoverTimer = null;
                  this._hoverDelay = true;
                  document
                    .querySelector(".header__menu")
                    ?.querySelectorAll("details.details-header-menu")
                    .forEach((d) => {
                      d._skipHoverDelay = true;
                    });
                }, 200)))
        : (clearTimeout(this._hoverTimer),
          (this._hoverTimer = null),
          (() => {
            const toEl = event.relatedTarget,
              host = event.currentTarget;
            const stillInMenu =
              !!toEl && (toEl === host || toEl.closest?.(".header__menu"));

            if (!stillInMenu) {
              document
                .querySelector(".header__menu")
                ?.querySelectorAll("details.details-header-menu")
                .forEach((d) => {
                  d._skipHoverDelay = false;
                });
            }
            const nextHasSubmenu = !!toEl
              ?.closest?.("li")
              ?.querySelector?.(".submenu");
            if (!nextHasSubmenu) {
              document
                .querySelector(".header__menu")
                ?.querySelectorAll("details.details-header-menu")
                .forEach((d) => {
                  d._skipHoverDelay = false;
                });
            }
            this._hoverDelay = false;
            this._delayEligible = false;
            this.open = false;
          })()));
  }
  fadeInDown() {
    Motion.animate(
      this.contentElement,
      {
        opacity: [0, 1],
      },
      { duration: 0.2, easing: [0.22, 0.61, 0.36, 1] },
    );
  }
  fadeInUp() {
    Motion.animate(
      this.contentElement,
      {
        opacity: [1, 0],
      },
      {
        duration: 0.2,
        delay: 0.12,
      },
    );
  }
}
(customElements.define("details-mega-menu", DetailsMegaMenu, {
  extends: "details",
}),
  megaMenuCount.set(DetailsMegaMenu, 0));
CustomElement.observeAndPatchCustomElements({
  "details-mega-menu": {
    tagElement: "details",
    classElement: DetailsMegaMenu,
  },
});

class SubMenuDetails extends HTMLDetailsElement {
  constructor() {
    (super(),
      (this.summaryElement = false),
      (this.contentElement = false),
      (this._open = false),
      (this.content = false),
      (this.boundSummaryClicked = this.onSummaryClicked.bind(this)),
      (this.detectHoverListener = this.detectHover.bind(this)),
      this.init());
  }

  get open() {
    return this._open;
  }

  set open(value) {
    value !== this._open &&
      ((this._open = value),
      this.isConnected
        ? this.transition(value)
        : value
          ? this.setAttribute("open", "")
          : this.removeAttribute("open"));
  }

  init() {
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute("open");
    this.content =
      this.closest(".menu-link")?.querySelector(".sub-children-menu");
    this.summaryElement?.addEventListener(
      "click",
      this.boundSummaryClicked,
    );
    if (window.innerWidth < 1025) {
      this.initialize();
    } else {
      this.addEventListener("mouseenter", this.detectHoverListener);
      this.addEventListener("mouseleave", this.detectHoverListener);
    }
  }

  disconnectedCallback() {
    this.summaryElement?.removeEventListener("click", this.boundSummaryClicked);
    this.removeEventListener("mouseenter", this.detectHoverListener);
    this.removeEventListener("mouseleave", this.detectHoverListener);
  }

  onSummaryClicked(event) {
    (event.preventDefault(),
      window.innerWidth >= 1025 &&
      this.hasAttribute("data-href") &&
      this.getAttribute("data-href").length > 0 &&
      (event.pointerType || this._open === true)
        ? (window.location.href = this.getAttribute("data-href"))
        : (this.open = !this.open));
  }

  async initialize() {
    if (this.content) {
      Motion.animate(
        this.content,
        this._open ? { height: "auto" } : { height: 0 },
        { duration: 0 },
      );
    }
  }

  detectHover(event) {
    event.type === "mouseenter" ? (this.open = !0) : (this.open = !1);
  }

  async transition(value) {
    return value
      ? (window.innerWidth < 1025
          ? Motion.animate(
              this.content,
              true ? { height: "auto" } : { height: 0 },
              { duration: 0.25 },
            )
          : this.closest("ul")
              ?.querySelectorAll("details")
              .forEach((details) => {
                details.removeAttribute("open");
                details._open = false;
              }),
        this.setAttribute("open", ""),
        (this._open = true))
      : (window.innerWidth < 1025
          ? Motion.animate(
              this.content,
              false ? { height: "auto" } : { height: 0 },
              { duration: 0.25 },
            )
          : "",
        this.removeAttribute("open"));
  }
}
customElements.define("submenu-details", SubMenuDetails, {
  extends: "details",
});
CustomElement.observeAndPatchCustomElements({
  "submenu-details": {
    tagElement: "details",
    classElement: SubMenuDetails,
  },
});

class CollapsibleRowDetails extends HTMLDetailsElement {
  constructor() {
    (super(),
      (this.summaryElement = null),
      (this.contentElement = null),
      (this._open = false),
      (this._hiddenMobile = false),
      (this.content = null),
      (this.boundSummaryClicked = this.onSummaryClicked.bind(this)),
      (this.mediaQuery = null),
      (this.boundMediaQueryChange = null),
      (this.closeTimer = null),
      this.init());
  }

  get open() {
    return this._open;
  }

  set open(value) {
    value !== this._open &&
      ((this._open = value),
      this.isConnected
        ? this.transition(value)
        : value
          ? this.setAttribute("open", "")
          : this.removeAttribute("open"));
  }

  init() {
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute("open");
    this._hiddenMobile = this.hasAttribute("hidden-mobile");
    this.content = this.querySelector(".collapsible-row__content");
    this.summaryElement?.addEventListener(
      "click",
      this.boundSummaryClicked,
    );
    this.initialize();
  }

  disconnectedCallback() {
    this.summaryElement?.removeEventListener("click", this.boundSummaryClicked);
    if (this.mediaQuery && this.boundMediaQueryChange) {
      this.mediaQuery.removeEventListener("change", this.boundMediaQueryChange);
      this.mediaQuery = null;
      this.boundMediaQueryChange = null;
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  onSummaryClicked(event) {
    (event.preventDefault(), (this.open = !this.open));
  }

  async initialize() {
    if (this._hiddenMobile) {
      const mediaQuery = window.matchMedia("(max-width: 767px)");
      const handleMediaQueryChange = NextSkyTheme.throttle((mediaQuery) => {
        if (!this.isConnected) return;
        if (mediaQuery.matches) {
          (this.removeAttribute("open"), this.classList.remove("detail-open"));
          this._open = false;
          if (this.content) {
            Motion.animate(this.content, { height: 0 }, { duration: 0 });
          }
        } else {
          (this.setAttribute("open", true), this.classList.add("detail-open"));
          this._open = true;
          if (this.content) {
            Motion.animate(this.content, { height: "auto" }, { duration: 0 });
          }
        }
      }, 100);
      handleMediaQueryChange(mediaQuery);
      this.mediaQuery = mediaQuery;
      this.boundMediaQueryChange = () => handleMediaQueryChange(mediaQuery);
      mediaQuery.addEventListener("change", this.boundMediaQueryChange);
    } else {
      if (this.content) {
        Motion.animate(
          this.content,
          this._open ? { height: "auto" } : { height: 0 },
          { duration: 0 },
        );
      }
    }
  }

  async transition(value) {
    if (!this.content) {
      return;
    }
    return value
      ? (Motion.animate(
          this.content,
          true ? { height: "auto" } : { height: 0 },
          { duration: 0.3 },
        ),
        this.classList.add("detail-open"),
        this.setAttribute("open", ""))
      : (Motion.animate(
          this.content,
          false ? { height: "auto" } : { height: 0 },
          { duration: 0.3 },
        ),
        this.classList.remove("detail-open"),
        this.open ||
          (this.closeTimer = setTimeout(() => {
            this.closeTimer = null;
            if (this.isConnected) this.removeAttribute("open");
          }, 300)));
  }
}
customElements.define("collapsible-row", CollapsibleRowDetails, {
  extends: "details",
});
CustomElement.observeAndPatchCustomElements({
  "collapsible-row": {
    tagElement: "details",
    classElement: CollapsibleRowDetails,
  },
});

class ProgressBar extends HTMLElement {
  constructor() {
    super();
    const orders = this.dataset.order;
    this.init(orders);
  }
  init(orders) {
    const min = Number(this.dataset.feAmount);
    const threshold = Number(this.dataset.threshold);
    if (threshold > orders) {
      this.classList.add("notify");
    } else {
      this.classList.remove("notify");
    }
    if (!min) return;
    if (!orders) return;
    const order = Number(orders);
    if (!order) return;
    if ((order / min) * 100 > 100) {
      this.setProgressBar(100);
    } else {
      this.setProgressBar((order / min) * 100);
    }
  }
  setProgressBar(progress) {
    const p = this.querySelector(".progress");
    if (!p) return;
    const p_bar = p.closest(".progress-bar");
    p.style.width = progress + "%";
    if (!p_bar) return;
    if (progress <= 0) {
      p_bar.classList.add("d-none");
    } else {
      p_bar.classList.remove("d-none");
    }
  }
}
customElements.define("progress-bar", ProgressBar);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.boundInputChange = this.onInputChange.bind(this);
    this.boundButtonClick = this.onButtonClick.bind(this);

    // Cache DOM elements
    this._buttons = this.querySelectorAll("button");
    this._mainProduct = this.closest(".sec__main-product");

    this.input?.addEventListener("change", this.boundInputChange);
    this._buttons.forEach((button) =>
      button.addEventListener("click", this.boundButtonClick),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = NextSkyTheme.subscribe(
      NextSkyTheme.PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  onInputChange() {
    this.validateQtyRules();
    this.syncEmbroideryInputs();
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.input) return;
    const previousValue = this.input.value;

    event.target.name === "plus" ||
    event.target.closest("button").name === "plus"
      ? this.input.stepUp()
      : this.input.stepDown();
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    // Use cached main product element
    if (this._mainProduct) {
      const mainProductInput = this._mainProduct.querySelector(
        "quantity-input input",
      );
      if (mainProductInput) {
        mainProductInput.value = this.input.value;
      }

      const sticky = this._mainProduct.querySelector(".sticky-add-cart");
      if (sticky) {
        const stickyInput = sticky.querySelector("quantity-input input");
        if (stickyInput) {
          stickyInput.value = this.input.value;
        }
      }
    }

    this.updateTotalPrice(this.input.value, this._mainProduct);
  }

  syncEmbroideryInputs() {
    if (!this.input) return;
    const embroideryId = (this.dataset.embroideryId || "").trim();
    if (!embroideryId) return;
    const escapedId =
      window.CSS && window.CSS.escape
        ? window.CSS.escape(embroideryId)
        : embroideryId.replace(/"/g, '\\"');
    const matchingInputs = Array.from(
      document.querySelectorAll(
        `quantity-input[data-embroidery-id="${escapedId}"]`,
      ),
    );
    matchingInputs.forEach((quantityInput) => {
      if (quantityInput === this) return;
      const otherInput = quantityInput.querySelector("input");
      if (!otherInput || otherInput.value === this.input.value) return;
      otherInput.value = this.input.value;
    });
  }

  updateTotalPrice(previousValue, mainProduct) {
    const form = this.closest("form");
    if (!form) return;
    const priceElement = form.querySelector(".total-price__detail");
    if (!priceElement) return;
    const dataTotalPrice = priceElement?.getAttribute("data-total-price");
    const totalPrice = Number(dataTotalPrice) * Number(previousValue);
    const sticky = form.classList.contains("form-sticky-add-cart");
    const mainProductPriceElement = mainProduct?.querySelector(
      ".product-detail__buy-buttons .total-price__detail",
    );
    if (sticky && mainProductPriceElement) {
      mainProductPriceElement.textContent = NextSkyTheme.formatMoney(
        totalPrice,
        themeGlobalVariables.settings.money_format,
      );
    }
    priceElement.textContent = NextSkyTheme.formatMoney(
      totalPrice,
      themeGlobalVariables.settings.money_format,
    );
  }

  validateQtyRules() {
    if (!this.input) return;
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const min = parseInt(this.input.min);
      // Cache button elements
      if (!this._buttonMinus) {
        this._buttonMinus = this.querySelector(
          ".quantity__button[name='minus']",
        );
      }
      if (this._buttonMinus) {
        this._buttonMinus.classList.toggle("disabled", value <= min);
      }
    }

    if (this.input.max) {
      const max = parseInt(this.input.max);
      // Cache button elements
      if (!this._buttonPlus) {
        this._buttonPlus = this.querySelector(".quantity__button[name='plus']");
      }
      if (this._buttonPlus) {
        this._buttonPlus.classList.toggle("disabled", value >= max);
      }
    }

    // Use cached main product element
    if (this._mainProduct) {
      const mainProductInput = this._mainProduct.querySelector(
        "quantity-input input",
      );
      if (mainProductInput) {
        mainProductInput.value = this.input.value;
      }

      const sticky = this._mainProduct.querySelector(".sticky-add-cart");
      if (sticky) {
        const stickyInput = sticky.querySelector("quantity-input input");
        if (stickyInput) {
          stickyInput.value = this.input.value;
        }
      }
    }
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
      this.quantityUpdateUnsubscriber = undefined;
    }
    this.input?.removeEventListener("change", this.boundInputChange);
    this._buttons?.forEach((button) =>
      button.removeEventListener("click", this.boundButtonClick),
    );
    // Clear cached elements
    this._buttons = null;
    this._mainProduct = null;
    this._buttonMinus = null;
    this._buttonPlus = null;
  }
}

customElements.define("quantity-input", QuantityInput);

class VideoSection extends HTMLElement {
  constructor() {
    super();
    this.thumb = null;
    this.video_iframe = null;
    this.boundIframeLoad = this.handleIframeLoad.bind(this);
    this.observer = null;
    this.videoSectionInitialized = false;
  }

  connectedCallback() {
    if (this.videoSectionInitialized) return;
    this.videoSectionInitialized = true;
    this.init();
  }

  init() {
    this.thumb = this.querySelector(".video-thumbnail");
    this.video_iframe = this.querySelector(".video-has-bg iframe");
    if (this.video_iframe) {
      this.video_iframe.addEventListener("load", this.boundIframeLoad);
    }
    if (typeof IntersectionObserver === "undefined") {
      this.querySelectorAll("iframe").forEach((video) => {
        const dataSrc = video.dataset.src;
        if (dataSrc) {
          video.src = dataSrc;
          video.removeAttribute("data-src");
        }
      });
      return;
    }
    const handleIntersection = (entries, observer) => {
      if (!this.isConnected || !entries[0].isIntersecting) return;
      observer.unobserve(this);
      const videos = this.querySelectorAll("iframe");
      videos.forEach((video) => {
        const dataSrc = video.dataset.src;
        if (dataSrc) {
          video.src = dataSrc;
          video.removeAttribute("data-src");
        }
      });
    };
    this.observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "0px 0px 200px 0px",
    });
    this.observer.observe(this);
  }

  handleIframeLoad() {
    if (this.thumb) {
      this.thumb.remove();
      this.thumb = null;
    }
  }

  disconnectedCallback() {
    this.video_iframe?.removeEventListener("load", this.boundIframeLoad);
    this.observer?.disconnect();
    this.observer = null;
    this.videoSectionInitialized = false;
  }
}
customElements.define("video-section", VideoSection);

class VideoLocal extends HTMLElement {
  constructor() {
    super();
    this.videoLocalTimeouts = new Set();
    this.videoLocalObservers = new Set();
    this.videoLocalCleanups = [];
    this.boundClickPlayVideo = this.clickPlayVideo.bind(this);
    this.videoLocalInitialized = false;
  }

  addManagedListener(target, type, handler, options) {
    if (!target) return;
    target.addEventListener(type, handler, options);
    this.videoLocalCleanups.push(() =>
      target.removeEventListener(type, handler, options),
    );
  }

  setManagedTimeout(callback, delay) {
    const timeoutId = setTimeout(() => {
      this.videoLocalTimeouts.delete(timeoutId);
      callback();
    }, delay);
    this.videoLocalTimeouts.add(timeoutId);
    return timeoutId;
  }

  addManagedObserver(observer) {
    if (!observer) return observer;
    this.videoLocalObservers.add(observer);
    return observer;
  }

  releaseManagedObserver(observer) {
    if (!observer) return;
    observer.disconnect();
    this.videoLocalObservers.delete(observer);
  }

  hydrateVideoElement(video) {
    if (!video) return false;
    let shouldLoad = false;
    const dataSrc = video.dataset.src;
    if (dataSrc) {
      video.src = dataSrc;
      video.removeAttribute("data-src");
      shouldLoad = true;
    }
    video.querySelectorAll("source[data-src]").forEach((source) => {
      if (!source.getAttribute("src")) {
        source.setAttribute("src", source.dataset.src);
        shouldLoad = true;
      }
    });
    if (shouldLoad && typeof video.load === "function") {
      video.load();
    }
    return shouldLoad;
  }

  connectedCallback() {
    if (this.videoLocalInitialized || !this.isConnected) return;
    this.videoLocalInitialized = true;
    this.init();
  }

  init() {
    this.setManagedTimeout(() => {
      if (!this.isConnected) return;
      this.loadContent();
    }, 100);
  }

  loadContentVideo(_this) {
    const width = window.innerWidth;
    if (_this.classList.contains("product__img_video") && width < 767) {
      return;
    }
    if (!_this.getAttribute("loaded") && _this.querySelector("template")) {
      const content = document.createElement("div");
      content.appendChild(
        _this
          .querySelector("template")
          .content.firstElementChild.cloneNode(true),
      );
      _this.setAttribute("loaded", true);
      const video = content.querySelector("video")
        ? content.querySelector("video")
        : content.querySelector("iframe");
      if (!video) return;
      const deferredElement = _this.appendChild(video);
      const alt = deferredElement.getAttribute("alt");
      const img = deferredElement.querySelector("img");
      if (alt && img) {
        img.setAttribute("alt", alt);
      }
      if (deferredElement.nodeName == "VIDEO") {
        this.hydrateVideoElement(deferredElement);
      }
      _this.thumb = _this.querySelector(".video-thumbnail");
      if (_this.thumb) {
        _this.thumb.remove();
      }
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        _this.safePlay(deferredElement);
      }
      const playButton = this.querySelector(".play-button");
      if (playButton) {
        this.addManagedListener(playButton, "click", this.boundClickPlayVideo, false);
      }
      // Add click handler for outfit-video-item to play/pause on video area click
      if (_this.classList.contains("outfit-video-item")) {
        const handleOutfitVideoClick = (e) => {
          if (
            e.target !== _this.querySelector(".play-button") &&
            !e.target.closest(".play-button")
          ) {
            const video = _this.querySelector("video");
            if (video) {
              if (video.paused) {
                _this.safePlay(video);
                const playButton = _this.querySelector(".play-button");
                if (playButton) playButton.classList.add("active");
              } else {
                video.pause();
                const playButton = _this.querySelector(".play-button");
                if (playButton) playButton.classList.remove("active");
              }
            }
          }
        };
        this.addManagedListener(_this, "click", handleOutfitVideoClick);
      }
    }
  }

  safePlay(video) {
    const p = video?.play?.();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        if (err?.name !== "AbortError") console.warn("play failed:", err);
      });
    }
  }

  safePause(media) {
    if (!media) return;
    if (media.classList?.contains("js-youtube")) {
      media.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        "*",
      );
      return;
    }
    if (media.classList?.contains("js-vimeo")) {
      media.contentWindow?.postMessage('{"method":"pause"}', "*");
      return;
    }
    media.pause?.();
  }

  loadContent(event) {
    if (event && event.currentTarget) {
      event.currentTarget.classList.add("active");
    }
    const _this = this;
    if (typeof IntersectionObserver === "undefined") {
      this.loadContentVideo(_this);
      return;
    }
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(_this);
      this.releaseManagedObserver(observer);
      this.loadContentVideo(_this);
      const videos = _this.querySelectorAll("video");
      videos.forEach((video) => {
        video.preload = "metadata";
        this.hydrateVideoElement(video);
        if (video.closest("video-product")) {
          if (video.autoplay || video.hasAttribute("autoplay")) {
            video.autoplay = false;
            video.removeAttribute("autoplay");
          }
          video.preload =
            video.preload === "none" ? "metadata" : video.preload || "metadata";

          const stop = () => {
            try {
              video.pause();
            } catch {}
            video.currentTime = 0;
          };

          video.addEventListener("loadeddata", stop, { once: true });
          video.addEventListener("playing", stop, { once: true });
          let timer = null;
          const onEnter = () => {
            clearTimeout(timer);
            timer = setTimeout(() => _this.safePlay(video), 60);
          };
          const onLeave = () => {
            clearTimeout(timer);
            if (!video.paused) video.pause();
          };
          this.videoLocalCleanups.push(() => clearTimeout(timer));
          this.addManagedListener(video, "pointerenter", onEnter);
          this.addManagedListener(video, "pointerleave", onLeave);
          this.addManagedListener(video, "focusin", onEnter);
          this.addManagedListener(video, "focusout", onLeave);
        }
        if (video.closest("quick-view-gallery")) {
          const videos = [...document.querySelectorAll("video")];
          videos.forEach((v) => {
            if (v !== video && !v.paused) {
              v.muted = true;
            }
          });
          video.muted = false;
        }
      });
    };
    const observerRootMargin = _this.matches("video-product")
      ? "0px"
      : "0px 0px 200px 0px";
    const observer = this.addManagedObserver(new IntersectionObserver(handleIntersection.bind(_this), {
      rootMargin: observerRootMargin,
    }));
    observer.observe(_this);
  }

  clickPlayVideo(event) {
    event.preventDefault();
    event.stopPropagation();
    const playButton = this.querySelector(".play-button");
    const video = this.querySelector("video");
    if (video) {
      if (video.paused) {
        this.safePlay(video);
        playButton?.classList.add("active");
      } else {
        video.pause();
        playButton?.classList.remove("active");
      }
    }
  }

  disconnectedCallback() {
    this.videoLocalTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.videoLocalTimeouts.clear();
    this.videoLocalObservers.forEach((observer) => observer.disconnect());
    this.videoLocalObservers.clear();
    this.videoLocalCleanups.forEach((cleanup) => cleanup());
    this.videoLocalCleanups = [];
    this.videoLocalInitialized = false;
  }
}
customElements.define("video-local", VideoLocal);

class VideoLocalPlay extends VideoLocal {
  constructor() {
    super();
  }
  init() {
    if (this.classList.contains("outfit-video-item")) {
      this.addManagedListener(this, "click", () => {
        this.querySelector("button")?.classList.add("active");
        this.loadContent();
      });
    } else {
      const poster = this.querySelector("button");
      if (!poster) return;
      this.addManagedListener(poster, "click", this.loadContent.bind(this));
    }
  }
}
customElements.define("video-local-play", VideoLocalPlay);

class VideoProduct extends VideoLocal {
  constructor() {
    super();
  }
  init() {
    this.setManagedTimeout(() => {
      if (!this.isConnected) return;
      this.loadContent();
    }, 100);
  }
}
customElements.define("video-product", VideoProduct);

class VideoLocalScroll extends VideoLocal {
  constructor() {
    super();
  }

  init() {
    if (window.Shopify && window.Shopify.designMode) {
      this.loadContentVideo(this);
      const videoElement = this.querySelector("video");
      if (videoElement) {
        this.hydrateVideoElement(videoElement);
      }
    } else {
      const _this = this;
      if (typeof IntersectionObserver === "undefined") {
        this.loadContentVideo(_this);
        const videoElement = this.querySelector("video");
        if (videoElement) {
          videoElement.preload = "metadata";
          this.hydrateVideoElement(videoElement);
        }
        return;
      }
      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(_this);
        this.releaseManagedObserver(observer);
        this.loadContentVideo(_this);
        const video = this.querySelector("video")
          ? this.querySelector("video")
          : this.querySelector("iframe");
        const videoElement = this.querySelector("video");
        if (videoElement) {
          videoElement.preload = "metadata";
          this.hydrateVideoElement(videoElement);
        }
        if (video) {
          const io = this.addManagedObserver(new IntersectionObserver(
            (entries) => {
              entries.forEach(
                ({ target, isIntersecting, intersectionRatio }) => {
                  if (isIntersecting && intersectionRatio > 0.2) {
                    this.safePlay(target);
                  } else {
                    this.safePause(target);
                  }
                },
              );
            },
            { threshold: [0, 0.2, 1] },
          ));
          io.observe(video);
        }
      };
      const observer = this.addManagedObserver(new IntersectionObserver(handleIntersection.bind(_this), {
        rootMargin: "0px 0px 200px 0px",
      }));
      observer.observe(_this);
    }
  }
}

customElements.define("video-local-scroll", VideoLocalScroll);

class VideoProductGallery extends VideoLocal {
  constructor() {
    super();
    this.playButton = this.querySelector("button.play-button");
    this.video = this.querySelector("video");
    if (this.playButton) {
      if (!this.playButton.classList.contains("active")) {
        if (this.video) {
          this.video.pause();
        }
      }
      this.addManagedListener(this.playButton, "click", () => {
        if (this.playButton.closest(".pswp__video-container")) {
          const playButton = this.querySelector(".play-button");
          const video = this.querySelector("video");
          if (video) {
            if (video.paused) {
              this.safePlay(video);
              playButton?.classList.add("active");
            } else {
              video.pause();
              playButton?.classList.remove("active");
            }
          }
        }
      });
    }
  }

  init() {
    if (this.hasAttribute("auto-play")) {
      this.setManagedTimeout(() => {
        if (!this.isConnected) return;
        this.loadContent();
      }, 100);
    } else {
      const poster = this.querySelector("button.play-video");
      if (!poster) return;
      this.addManagedListener(poster, "click", () => {
        NextSkyTheme.pauseAllMedia(this.closest("media-gallery"));
        this.loadContent();
        if (this.playButton) {
          this.playButton.classList.remove("hidden");
          this.playButton.classList.add("active");
        }
      });
    }
  }
}
customElements.define("video-product-gallery", VideoProductGallery);

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.animationDuration = 300;
    this.isAnimating = false;
    this.animationFrame = null;
    this.initFrame = null;
    this.boundCloseClick = this.onCloseClick.bind(this);
    this.boundKeyDown = this.onKeyDown.bind(this);
  }

  connectedCallback() {
    if (this.initFrame) cancelAnimationFrame(this.initFrame);
    this.initFrame = requestAnimationFrame(() => {
      this.initFrame = null;
      this.init();
    });
  }

  init() {
    this._naturalHeight = this.offsetHeight || 0;
    this.closeButton = this.querySelector(".announcement-bar__close");
    this.parentSection = this.closest(".section-announcement-bar");
    if (!this.closeButton || !this.parentSection) return;
    if (this.closeButton) {
      this.closeButton.removeEventListener("click", this.boundCloseClick);
      this.closeButton.removeEventListener("keydown", this.boundKeyDown);
      this.closeButton.addEventListener("click", this.boundCloseClick);
      this.closeButton.addEventListener("keydown", this.boundKeyDown);
    }
  }

  onKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.onCloseClick(event);
    }
  }

  onCloseClick(event) {
    event.preventDefault();
    if (this.isAnimating) return;
    this.isAnimating = true;
    if (!this._naturalHeight || this._naturalHeight <= 0) {
      this._naturalHeight = this.offsetHeight;
    }
    this.parentSection.classList.add("announcement-closing");
    const startHeight = this._naturalHeight;
    const startTime = performance.now();
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / this.animationDuration, 1);
      const eased = this.easeOutCubic(progress);
      const currentHeight = startHeight * (1 - eased);
      const currentOpacity = 1 - eased;
      this.parentSection.style.height = `${currentHeight}px`;
      this.parentSection.style.opacity = String(currentOpacity);
      this.classList.add("d-none");
      document.body.style.removeProperty("--height-bar");
      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.finishClosingAnimation();
      }
    };
    this.animationFrame = requestAnimationFrame(animate);
  }

  finishClosingAnimation() {
    this.parentSection.style.height = "0px";
    this.parentSection.style.opacity = "0";
    this.parentSection.classList.remove("announcement-closing");
    this.parentSection.classList.add("announcement-closed");
    NextSkyTheme.setCookie("announcement_closed", "true", 1);
    this.isAnimating = false;
  }

  easeOutCubic(x) {
    return 1 - Math.pow(1 - x, 3);
  }

  disconnectedCallback() {
    if (this.initFrame) {
      cancelAnimationFrame(this.initFrame);
      this.initFrame = null;
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    if (this.closeButton) {
      this.closeButton.removeEventListener("click", this.boundCloseClick);
      this.closeButton.removeEventListener("keydown", this.boundKeyDown);
    }
  }
}
customElements.define("announcement-bar", AnnouncementBar);

class CarouselMobile extends HTMLElement {
  constructor() {
    super();
    this.enable = this.dataset.enableCarouselMobile == "true";
    this.isMulticontent = this.dataset.multicontent == "true";
    this.isFlexibleSection = this.dataset.flexibleSection == "true";
    this.showPagination = this.dataset.showPagination == "true";
    this.bundle = this.dataset.bundle == "true";
    this.swiperSlideInnerHtml = this.innerHTML;
    this.mobileScreen = Number(this.dataset.mobileScreen) || 767;
    this.swiper = null;
    this.cleanupTimeout = null;
    this.initCarousel();
  }

  initCarousel() {
    if (this.enable) {
      let width = window.innerWidth;
      // Debounce resize event for better performance
      this.debouncedResize = NextSkyTheme.debounce(() => {
        const newWidth = window.innerWidth;
        if (newWidth <= this.mobileScreen && width > this.mobileScreen) {
          this.actionOnMobile();
        }
        if (newWidth > this.mobileScreen && width <= this.mobileScreen) {
          this.actionOutMobile();
        }
        width = newWidth;
      }, 250);

      window.addEventListener("resize", this.debouncedResize, {
        passive: true,
      });

      if (width <= this.mobileScreen) {
        this.actionOnMobile();
      } else {
        this.actionOutMobile();
      }
    }
  }

  actionOnMobile() {
    this.classList.add("swiper");
    let html;
    if (this.isFlexible) {
      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = this.swiperSlideInnerHtml;
      const firstLevelDivs = tempContainer.querySelectorAll(":scope > div");
      firstLevelDivs.forEach((childDiv) => {
        if (childDiv.classList.contains("switch-slide__mobile")) {
          childDiv.classList.remove("switch-slide__mobile");
          childDiv.classList.add("swiper-slide");
        }
      });
      html = tempContainer.innerHTML;
    } else {
      html = this.swiperSlideInnerHtml.replaceAll(
        "switch-slide__mobile",
        "swiper-slide",
      );
    }
    const wrapper = `<div class='swiper-wrapper'>${html}</div>${
      this.showPagination
        ? '<div class="swiper-pagination" style="--swiper-pagination-bottom: 0"></div>'
        : ""
    }`;
    this.destroySlide();
    this.innerHTML = wrapper;
    initSlideOnDemand(this).then((swiper) => {
      if (this.isConnected) {
        this.swiper = swiper;
      }
    }).catch((error) => console.warn("carousel mobile init failed:", error));
    new LazyLoader(".image-lazy-load");
  }

  actionOutMobile() {
    this.destroySlide();
    new LazyLoader(".image-lazy-load");
    this.classList.remove("swiper");
    this.innerHTML = this.swiperSlideInnerHtml;
    if (this.bundle) {
      this.className = "";
      this.cleanupTimeout = setTimeout(() => {
        this.classList.remove("swiper-backface-hidden");
        this.cleanupTimeout = null;
      }, 100);
      return;
    }
    new LazyLoader(".image-lazy-load");
  }

  destroySlide() {
    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
      this.cleanupTimeout = null;
    }
    if (Array.isArray(this._slideCleanups)) {
      this._slideCleanups.forEach((cleanup) => cleanup());
      this._slideCleanups = [];
    }
    if (
      this.swiper &&
      typeof this.swiper.destroy === "function" &&
      !this.swiper.destroyed
    ) {
      this.swiper.destroy(true, true);
    }
    this.swiper = null;
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.debouncedResize) {
      window.removeEventListener("resize", this.debouncedResize);
    }
    this.destroySlide();
  }
}
customElements.define("carousel-mobile", CarouselMobile);

class CarouselBlock extends HTMLElement {
  constructor() {
    super();
    this.showPagination = this.dataset.showPagination === "true";
    this.swiper = null;
  }

  connectedCallback() {
    if (this.swiper) return;
    this.initCarousel();
  }

  initCarousel() {
    const wrapper = this.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    const firstLevelDivs = wrapper.querySelectorAll(":scope > div");
    firstLevelDivs.forEach((childDiv) => {
      if (childDiv.classList.contains("switch-slide__mobile")) {
        childDiv.classList.remove("switch-slide__mobile");
        childDiv.classList.add("swiper-slide");
      }
    });

    initSlideOnDemand(this).then((swiper) => {
      if (this.isConnected) {
        this.swiper = swiper;
      }
    }).catch((error) => console.warn("carousel block init failed:", error));
    new LazyLoader(".image-lazy-load");
  }

  disconnectedCallback() {
    if (Array.isArray(this._slideCleanups)) {
      this._slideCleanups.forEach((cleanup) => cleanup());
      this._slideCleanups = [];
    }
    if (
      this.swiper &&
      typeof this.swiper.destroy === "function" &&
      !this.swiper.destroyed
    ) {
      this.swiper.destroy(true, true);
    }
    this.swiper = null;
  }
}

customElements.define("carousel-block", CarouselBlock);

class NavBar extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    document.body.classList.add("mobile-sticky-bar-enabled");
    // Cache site header to avoid repeated queries
    this._siteHeader = document.querySelector(".site-header");
    const header = this._siteHeader.querySelector("header");
    this._dataScroll = header.dataset.stickyType;
  }

  connectedCallback() {
    // Throttle scroll event for better performance
    this.throttledScroll = NextSkyTheme.throttle(
      this.updateScrollNavigationbar.bind(this),
      16,
    );
    window.addEventListener("scroll", this.throttledScroll, { passive: true });
  }

  updateScrollNavigationbar() {
    const scrollTop =
      document.documentElement.scrollTop || document.body.scrollTop;
    const scrolledTo = window.scrollY + window.innerHeight;
    const threshold = this.offsetHeight;
    const isReachBottom = document.body.scrollHeight - threshold <= scrolledTo;
    if (isReachBottom) {
      this.classList.add("show");
      return;
    }
    if (scrollTop > 200) {
      if (this._dataScroll == "always") {
        this.classList.add("show");
        NextSkyTheme.getRoot().classList.add("mobile-sticky-bar-active");
      } else if (
        this._siteHeader &&
        this._siteHeader.classList.contains("animate") &&
        this._siteHeader.classList.contains("header-sticky-hidden")
      ) {
        this.classList.add("show");
        NextSkyTheme.getRoot().classList.add("mobile-sticky-bar-active");
      } else if (
        this._siteHeader &&
        this._siteHeader.classList.contains("animate") &&
        !this._siteHeader.classList.contains("header-sticky-hidden")
      ) {
        this.classList.remove("show");
        NextSkyTheme.getRoot().classList.remove("mobile-sticky-bar-active");
      } else if (!this._siteHeader) {
        this.classList.add("show");
        NextSkyTheme.getRoot().classList.add("mobile-sticky-bar-active");
      } else {
        this.classList.add("show");
        NextSkyTheme.getRoot().classList.add("mobile-sticky-bar-active");
      }
    } else {
      this.classList.remove("show");
      NextSkyTheme.getRoot().classList.remove("mobile-sticky-bar-active");
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.throttledScroll) {
      window.removeEventListener("scroll", this.throttledScroll);
    }
    // Clear cached elements
    this._siteHeader = null;
  }
}
customElements.define("mobile-navigation-bar", NavBar);

class ImageComparison extends HTMLElement {
  constructor() {
    super();
    this.container = this;
    this.slider = this.querySelector(".slider");
    this.overlay = this.querySelector(".image-after");
    this.handle = this.querySelector(".handle");
    this.x = 0;
    this.boundary = 300;
    this.mixClipPath;
    this.mixSliderColor;
    this.step = 50;
    this.elastic = 0.1;
    this.animated = false;
    this.observer = null;

    this.isHovering = false;
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleResize = this.handleResize.bind(this);
    this.boundSliderFocus = () => {
      document.addEventListener("keydown", this.boundHandleKeyDown);
    };
    this.boundSliderBlur = () => {
      document.removeEventListener("keydown", this.boundHandleKeyDown);
    };
    this.boundHandleOrientationChange = () => {
      if (this.orientationTimeout) {
        clearTimeout(this.orientationTimeout);
      }
      this.orientationTimeout = setTimeout(() => {
        this.orientationTimeout = null;
        this.handleResize();
      }, 200);
    };

    if (!this.slider || !this.overlay || !this.handle) return;
    this.init();
    this.setupDrag();

    this.slider.addEventListener("focus", this.boundSliderFocus);

    this.slider.addEventListener("blur", this.boundSliderBlur);

    window.addEventListener("resize", this.boundHandleResize);

    window.addEventListener("orientationchange", this.boundHandleOrientationChange);

    this.setupIntersectionObserver();
  }

  updateSliderStatus() {
    const swiperContainer = this.closest("slide-section");
    if (!swiperContainer) return;
    const autoplay = swiperContainer?.dataset.autoplay === "true";

    const swiperInstance = swiperContainer.swiper;
    if (!swiperInstance) return;

    swiperInstance.allowTouchMove = !this.isHovering;

    if (this.isHovering) {
      swiperInstance.allowSlideNext = false;
      swiperInstance.allowSlidePrev = false;
    } else {
      swiperInstance.allowSlideNext = true;
      swiperInstance.allowSlidePrev = true;
    }
    if (autoplay) {
      if (this.isHovering) {
        swiperInstance.autoplay.stop();
      } else {
        swiperInstance.autoplay.start();
      }
    }
    swiperInstance.update();
  }

  handleResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      const newWidth = this.container.clientWidth;
      const newFullBoundary = newWidth / 2;

      if (
        !this.fullBoundary ||
        Math.abs(this.fullBoundary - newFullBoundary) > 5
      ) {
        this.init();
        this.moveSlider(0);
      }
    }, 100);
  }

  init() {
    const handleWidth = this.handle ? this.handle.offsetWidth : 0;
    this.fullBoundary = this.container.clientWidth / 2;

    const offset = 1;
    this.boundary = this.fullBoundary - handleWidth / 2 + offset;

    this.mixClipPath = Motion.transform(
      [-this.fullBoundary, this.fullBoundary],
      ["inset(0% 0% 0% 0%)", "inset(0% 0% 0% 100%)"],
    );

    this.mixSliderColor = Motion.transform(
      [
        -this.boundary + 20,
        -this.boundary + 60,
        this.boundary - 60,
        this.boundary - 20,
      ],
      [
        "rgba(255, 255, 255)",
        "rgba(255, 255, 255)",
        "rgba(255, 255, 255)",
        "rgba(255, 255, 255)",
      ],
    );

    const isRtl = document.documentElement.dir === "rtl";
    if (!this.animated) {
      const startPosition = isRtl
        ? this.boundary - this.boundary * 0.2
        : -this.boundary + this.boundary * 0.2;
      this.moveSlider(startPosition);
    } else {
      this.moveSlider(0);
    }
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.animated) {
            this.runEntranceAnimation();
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.2,
      },
    );
    this.observer.observe(this);
  }

  runEntranceAnimation() {
    this.animated = true;
    const isRtl = document.documentElement.dir === "rtl";
    const startPosition = isRtl
      ? this.boundary - this.boundary * 0.2
      : -this.boundary + this.boundary * 0.2;
    Motion.animate(startPosition, 0, {
      onUpdate: (value) => this.moveSlider(value),
      type: "spring",
      stiffness: 80,
      damping: 20,
      duration: 0.8,
      delay: 0.2,
    });
  }

  moveSlider(newX) {
    this.x = newX;
    const clipPathX = Motion.clamp(-this.fullBoundary, this.fullBoundary, newX);
    this.overlay.style.clipPath = this.mixClipPath(clipPathX);
    this.slider.style.transform = `translateX(${this.x}px)`;
    this.slider.style.backgroundColor = this.mixSliderColor(this.x);
  }

  handleKeyDown(event) {
    if (!this.slider.matches(":focus")) return;
    let moveBy = 0;

    if (event.key === "ArrowLeft") {
      moveBy = -this.step;
    } else if (event.key === "ArrowRight") {
      moveBy = this.step;
    } else {
      return;
    }

    Motion.animate(
      this.x,
      Motion.clamp(-this.boundary, this.boundary, this.x + moveBy),
      {
        onUpdate: (value) => this.moveSlider(value),
        type: "spring",
        stiffness: 900,
        damping: 40,
        velocity: moveBy * 10,
      },
    );
  }

  setupDrag() {
    let startX = 0;
    let startClientX = 0;
    let newX = 0;
    let isDragging = false;
    let dragBoundary = 0;
    const _this = this;
    function updateX() {
      if (!_this.isConnected) return;
      _this.moveSlider(newX);
    }

    this.boundPointerDown = (e) => {
      if (!this.isConnected) return;
      startX = this.x;
      startClientX = e.clientX;
      isDragging = true;
      dragBoundary = this.fullBoundary;
      document.body.style.cursor = "grabbing";
      this.slider.style.cursor = "grabbing";
      this.slider.classList.add("active");
      this.slider.setPointerCapture(e.pointerId);
      this.isHovering = true;
      this.updateSliderStatus();
    };

    this.boundPointerMove = (e) => {
      if (!this.isConnected) return;
      if (!this.slider.hasPointerCapture(e.pointerId) || !isDragging) return;

      const deltaX = e.clientX - startClientX;
      newX = startX + deltaX;

      const handleWidth = this.handle ? this.handle.offsetWidth : 0;
      const offset = 1;
      const maxPosition = dragBoundary - handleWidth / 2;
      const minPosition = -dragBoundary + handleWidth / 2 - offset;
      newX = Motion.clamp(minPosition, maxPosition, newX);
      Motion.frame.render(updateX);
    };

    this.boundPointerUp = (e) => {
      if (!this.isConnected) return;
      if (!this.slider.hasPointerCapture(e.pointerId)) return;
      this.slider.releasePointerCapture(e.pointerId);
      isDragging = false;
      document.body.style.cursor = "default";
      this.slider.style.cursor = "grab";
      this.slider.classList.remove("active");
      if (this.x < -this.boundary || this.x > this.boundary) {
        const targetX =
          this.x < -this.boundary ? -this.boundary : this.boundary;
        Motion.animate(this.x, targetX, {
          onUpdate: (value) => this.moveSlider(value),
          type: "spring",
          stiffness: 900,
          damping: 40,
        });
      }
      this.isHovering = false;
      this.updateSliderStatus();
    };

    this.slider.addEventListener("pointerdown", this.boundPointerDown);
    this.slider.addEventListener("pointermove", this.boundPointerMove);
    this.slider.addEventListener("pointerup", this.boundPointerUp);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    if (this.orientationTimeout) {
      clearTimeout(this.orientationTimeout);
      this.orientationTimeout = null;
    }

    window.removeEventListener("resize", this.boundHandleResize);
    window.removeEventListener("orientationchange", this.boundHandleOrientationChange);
    document.removeEventListener("keydown", this.boundHandleKeyDown);
    this.slider?.removeEventListener("focus", this.boundSliderFocus);
    this.slider?.removeEventListener("blur", this.boundSliderBlur);
    this.slider?.removeEventListener("pointerdown", this.boundPointerDown);
    this.slider?.removeEventListener("pointermove", this.boundPointerMove);
    this.slider?.removeEventListener("pointerup", this.boundPointerUp);
  }
}
if (!customElements.get("image-comparison")) {
  customElements.define("image-comparison", ImageComparison);
}

class AskQuestion extends HTMLButtonElement {
  constructor() {
    super();
    this.boundClick = this.onClick.bind(this);
    this.openTimer = null;
    this.currentPopup = null;
    this.init();
  }
  init() {
    this.addEventListener("click", this.boundClick, false);

    const urlInfo = window.location.href;
    const newURL = location.href.split("?")[0];
    if (urlInfo.indexOf("contact_posted=true#ContactFormAsk") >= 1) {
      notifier.show(window.message.ask_question.success, "success", 4000);
      window.history.pushState("object", document.title, newURL);
    }
  }
  onClick(e) {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }

    const ask_question = e.target
      .closest(".ask-question")
      ?.querySelector("template");
    this.removePendingPopup();
    let popup = null;
    if (ask_question) {
      const content = document.createElement("div");
      content.appendChild(
        ask_question.content.firstElementChild.cloneNode(true),
      );
      popup = content.querySelector("ask-question-popup");
      if (popup) {
        NextSkyTheme.getBody().appendChild(popup);
        this.currentPopup = popup;
      }
    }
    if (!popup) return;
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      if (this.isConnected && popup.isConnected) {
        eventModal(popup, "open", true);
      }
    }, 100);
    NextSkyTheme.global.rootToFocus = this;
  }

  removePendingPopup() {
    if (!this.currentPopup || this.currentPopup.classList.contains("active")) {
      return;
    }

    this.currentPopup.remove();
    this.currentPopup = null;
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundClick);
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.removePendingPopup();
    this.currentPopup = null;
  }
}
customElements.define("ask-question", AskQuestion, {
  extends: "button",
});
CustomElement.observeAndPatchCustomElements({
  "ask-question": {
    tagElement: "button",
    classElement: AskQuestion,
  },
});
class SocialShare extends HTMLElement {
  constructor() {
    super();
    this.cleanupHandlers = [];
    this.init();
  }
  init() {
    this.cleanupHandlers.forEach((cleanup) => cleanup());
    this.cleanupHandlers = [];
    this.querySelectorAll(".blog-sharing .btn-sharing").forEach((share) => {
      const handleClick = (event) => {
        event.preventDefault();
        const target = event.currentTarget;
        const social = target.getAttribute("data-social");
        const nameSocial = target.getAttribute("data-name");
        window.open(social, nameSocial, "height=500,width=500");
      };
      share.addEventListener("click", handleClick, false);
      this.cleanupHandlers.push(() =>
        share.removeEventListener("click", handleClick),
      );
    });
  }

  disconnectedCallback() {
    this.cleanupHandlers.forEach((cleanup) => cleanup());
    this.cleanupHandlers = [];
  }
}
customElements.define("social-share", SocialShare);

class LazyLoadTemplate extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.init();
  }

  init() {
    if (this.querySelector("template")) {
      const handleIntersection = async (entries, observer) => {
        if (!this.isConnected || !entries[0].isIntersecting) return;
        observer.unobserve(this);
        observer.disconnect();
        this.observer = null;
        const content = document.createElement("div");
        const template = this.querySelector("template");
        if (!template?.content?.firstElementChild) return;
        content.appendChild(template.content.firstElementChild.cloneNode(true));
        const html = content.querySelector(".product__countdown")
          ? content.querySelector(".product__countdown")
          : content.querySelector(".product_scrolling");
        await import(importJs.countdownTimer);
        if (!this.isConnected || !html) return;
        this.parentNode.insertBefore(html, this.nextSibling);
        this.remove();
      };
      this.observer = new IntersectionObserver(handleIntersection, {
        rootMargin: "0px 0px 200px 0px",
      });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }
}
customElements.define("lazy-load-template", LazyLoadTemplate);

class ScrollingEffect extends HTMLElement {
  constructor() {
    super();
    this.scrollCleanups = [];
    this.initialized = false;
  }

  connectedCallback() {
    this.init();
  }

  get contents() {
    return Array.from(this.children);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.contents.forEach((element) => {
      const content =
        element.querySelector(".block__heading") ||
        element.querySelector(".block__description") ||
        element.querySelector(".block__buttons");

      if (!content) return;

      const animation = Motion.animate(content, {
        opacity: [0, 0, 1, 1, 1, 0, 0],
      });
      const scrollControl = Motion.scroll(animation, {
        target: content,
        offsets: ["33vh", "66vh"],
      });

      this.scrollCleanups.push(() => {
        if (typeof scrollControl === "function") {
          scrollControl();
        } else if (typeof scrollControl?.stop === "function") {
          scrollControl.stop();
        } else if (typeof scrollControl?.cancel === "function") {
          scrollControl.cancel();
        }

        if (typeof animation?.cancel === "function") {
          animation.cancel();
        }
      });
    });
  }

  disconnectedCallback() {
    this.scrollCleanups.forEach((cleanup) => {
      try {
        cleanup();
      } catch (err) {
        console.warn("scrolling effect cleanup failed:", err);
      }
    });
    this.scrollCleanups = [];
    this.initialized = false;
  }
}
customElements.define("scrolling-effect", ScrollingEffect);
class MotionEffect extends HTMLElement {
  constructor() {
    super();
    this.inViewStop = null;
    this.effectTimer = null;
  }

  async connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    if (typeof this.inViewStop === "function") {
      this.inViewStop();
    }
    this.initAnimate();
    this.inViewStop = Motion.inView(
      this,
      async () => {
        if (this.mediaElements) await loadImages(this.mediaElements);
        if (!this.isConnected) return;
        this.effectTimer = setTimeout(() => {
          this.effectTimer = null;
          if (this.isConnected) {
            this.initAnimateEffect();
          }
        }, 100);
      },
      {
        margin: this.animationConfig.rayo.margin,
      },
    );
  }

  disconnectedCallback() {
    if (typeof this.inViewStop === "function") {
      this.inViewStop();
      this.inViewStop = null;
    }
    if (this.effectTimer) {
      clearTimeout(this.effectTimer);
      this.effectTimer = null;
    }
  }

  get scrollAnimation() {
    return themeGlobalVariables.settings.scrollAnimation;
  }

  get slideShowElements() {
    return this.hasAttribute("slideshow") ? true : false;
  }

  get mediaElements() {
    return this.querySelectorAll("img, svg");
  }

  get animateEffect() {
    return this.dataset.animate || "";
  }

  get delay() {
    return parseInt(this.dataset.animateDelay || 0) / 1000;
  }

  getMediaElement() {
    return this.querySelector("img");
  }

  getPlaceholderElement() {
    return this.querySelector("svg");
  }

  getMedia() {
    return this.getMediaElement() !== null
      ? this.getMediaElement()
      : this.getPlaceholderElement();
  }

  get delayLoad() {
    return this.classList.contains("animate-delay");
  }

  get delayMedia() {
    return this.closest(".block__media-gallery")
      ? this.closest(".block__media-gallery").classList.contains("insert")
      : false;
  }

  get animationConfig() {
    return {
      slideInLeft: {
        clipPath: "inset(0% 0% 0% 0%)",
        duration: 0.8,
        easing: [0.422, 0.117, 0.572, 0.786],
      },
      rayo: {
        ease: [0.22, 0.61, 0.36, 1],
        dIn: 0.8,
        dOut: 0.65,
        margin: "0px 0px -30px 0px",
        upY: 25,
        zx: 0,
        zy: 0,
        zScale: 1.06,
      },
      motion: {
        easing: [0.22, 0.61, 0.36, 1],
        dIn: 1.1,
        dOut: 0.7,
      },
    };
  }

  async initAnimate() {
    if (
      this.scrollAnimation == "desktop_only" &&
      window.innerWidth < 768 &&
      !this.slideShowElements
    ) {
      this.classList.remove("loading-motion");
      return;
    }
    if (this.delayMedia) {
      this.classList.remove("loading-motion");
      return;
    }
    switch (this.animateEffect) {
      case "highlighted-text":
        await this.highlightedTextInitial();
        break;
      case "left-to-right":
        await this.leftToRightInitial();
        break;
      case "fade-in":
        await this.fadeInInitial();
        break;
      case "fade-up":
        await this.fadeUpInitial();
        break;
      case "zoom-in":
        await this.zoomInInitial();
        break;
      case "zoom-in-big":
        await this.zoomInBigInitial();
        break;
      case "zoom-out":
        await this.zoomOutInitial();
        break;
      case "zoom-out-small":
        await this.zoomOutSmallInitial();
        break;
      case "slide-in-left":
        await this.slideInLeftInitial();
        break;
    }
    this.classList.remove("loading-motion");
  }

  leftToRightInitial() {
    Motion.animate(this, { opacity: 0 }, { duration: 0 });
  }

  fadeInInitial() {
    Motion.animate(this, { opacity: 0 }, { duration: 0 });
  }

  highlightedTextInitial() {
    this.animateSvgInitial();
  }

  fadeUpInitial() {
    this.style.willChange = "transform, opacity";
    Motion.animate(
      this,
      {
        transform: `translateY(${this.animationConfig.rayo.upY}px)`,
        opacity: 0,
      },
      { duration: 0 },
    );

    this._reverseToInitial = () => {
      Motion.animate(
        this,
        {
          transform: `translateY(${this.animationConfig.rayo.upY}px)`,
          opacity: 0,
        },
        {
          duration: this.animationConfig.rayo.dOut,
          easing: this.animationConfig.rayo.ease,
        },
      );
    };
    this.animateSvgInitial();
  }

  zoomInInitial() {
    Motion.animate(this, { transform: "scale(0.8)" }, { duration: 0 });
  }

  zoomInBigInitial() {
    Motion.animate(this, { transform: "scale(0)" }, { duration: 0 });
  }

  zoomOutSmallInitial() {
    if (window.innerWidth < 768) return;
    Motion.animate(
      this,
      {
        transform: `translate(${this.animationConfig.rayo.zx}px, ${this.animationConfig.rayo.zy}px) scale(${this.animationConfig.rayo.zScale})`,
      },
      { duration: 0 },
    );
  }
  zoomOutInitial() {
    if (window.innerWidth < 768) return;
    Motion.animate(
      this,
      { transform: "scale(1.1)", opacity: 1 },
      { duration: 0 },
    );
  }
  slideInLeftInitial() {
    Motion.animate(
      this,
      {
        clipPath: "inset(0% 100% 0% 0%)",
      },
      { duration: 0 },
    );
  }

  async initAnimateEffect() {
    if (
      this.scrollAnimation == "desktop_only" &&
      window.innerWidth < 768 &&
      !this.slideShowElements
    )
      return;

    if (this.delayMedia || this.delayLoad) return;

    switch (this.animateEffect) {
      case "highlighted-text":
        await this.highlightedText();
        break;
      case "fade-up":
        await this.fadeUp();
        break;
      case "zoom-out-small":
        await this.zoomOutSmall();
        break;
      case "left-to-right":
        this.leftToRight();
        break;
      case "fade-in":
        await this.fadeIn();
        break;
      case "zoom-in":
        await this.zoomIn();
        break;
      case "zoom-in-big":
        await this.zoomInBig();
        break;
      case "zoom-out":
        await this.zoomOut();
        break;
      case "slide-in-left":
        this.slideInLeft();
        break;
    }
    if (this.slideShowElements) {
      this.classList.add("animate-delay");
    }
  }

  async highlightedText() {
    await this.animateSvg();
  }

  async fadeUp() {
    await Motion.animate(
      this,
      { transform: "translateY(0px)", opacity: 1 },
      {
        duration: 0.5,
        delay: this.delay || 0,
        easing: this.animationConfig.rayo.ease,
      },
    ).finished;
    this.animateSvg();
  }

  async leftToRight() {
    await Motion.animate(this, { opacity: 1 }).finished;
    this.classList.add("show");
  }

  async fadeIn() {
    await Motion.animate(
      this,
      { opacity: 1 },
      {
        duration: 0.4,
        delay: this.delay,
        easing: this.animationConfig.motion.easing,
      },
    ).finished;
  }

  async zoomIn() {
    await Motion.animate(
      this,
      { transform: "scale(1)" },
      {
        duration: this.animationConfig.motion.dIn,
        delay: this.delay,
        easing: this.animationConfig.motion.easing,
      },
    ).finished;
  }

  async zoomInBig() {
    await Motion.animate(
      this,
      { transform: "scale(1)" },
      {
        duration: this.animationConfig.motion.dIn,
        delay: this.delay,
        easing: this.animationConfig.motion.easing,
      },
    ).finished;
  }

  async zoomOut() {
    if (window.innerWidth < 768) return;
    await Motion.animate(
      this,
      { transform: "scale(1)" },
      {
        duration: this.animationConfig.motion.dIn,
        delay: this.delay,
        easing: this.animationConfig.motion.easing,
      },
    ).finished;
  }

  async zoomOutSmall() {
    if (window.innerWidth < 768) return;
    Motion.animate(
      this,
      {
        transform: [
          `translate(${this.animationConfig.rayo.zx}px, ${this.animationConfig.rayo.zy}px) scale(${this.animationConfig.rayo.zScale})`,
          "translate(0px, 0px) scale(1)",
        ],
      },
      {
        duration: this.animationConfig.rayo.dIn,
        delay: this.delay || 0,
        easing: this.animationConfig.rayo.ease,
      },
    ).finished;
  }

  async slideInLeft() {
    Motion.animate(
      this.getMedia(),
      {
        clipPath: this.animationConfig.slideInLeft.clipPath,
      },
      {
        duration: this.animationConfig.slideInLeft.duration,
        easing: this.animationConfig.slideInLeft.easing,
        delay: this.delay,
      },
    ).finished;
  }

  animateSvgInitial() {
    const svg = this.querySelectorAll("svg");
    svg.forEach((el) => {
      el.classList.remove("animate");
    });
  }

  animateSvg() {
    const svg = this.querySelectorAll("svg");
    svg.forEach((el) => {
      el.classList.add("animate");
    });
  }
}
customElements.define("motion-effect", MotionEffect);
class MotionItemsEffect extends HTMLElement {
  constructor() {
    super();
    this.inViewStop = null;
    this.setupInitialAnimation();
    this.setupInViewEffect();
  }

  get scrollAnimation() {
    return themeGlobalVariables.settings.scrollAnimation;
  }

  get allItems() {
    return this.querySelectorAll(".motion-item");
  }

  get visibleItems() {
    return this.querySelectorAll(".product-item:not([style])");
  }

  setupInitialAnimation() {
    if (this.scrollAnimation == "desktop_only" && window.innerWidth < 768) {
      return;
    }
    Motion.animate(
      this.allItems,
      {
        y: 30,
        opacity: 0,
        visibility: "hidden",
      },
      {
        duration: 0,
      },
    );
  }

  setupInViewEffect() {
    if (this.scrollAnimation == "desktop_only" && window.innerWidth < 768) {
      return;
    }
    if (typeof this.inViewStop === "function") {
      this.inViewStop();
    }
    this.inViewStop = Motion.inView(this, this.animateItems.bind(this), {
      margin: "0px 0px -50px 0px",
    });
  }

  disconnectedCallback() {
    if (typeof this.inViewStop === "function") {
      this.inViewStop();
      this.inViewStop = null;
    }
  }

  animateItems() {
    Motion.animate(
      this.allItems,
      {
        y: [30, 0],
        opacity: [0, 1],
        visibility: ["hidden", "visible"],
      },
      {
        duration: 0.5,
        delay: Motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      },
    ).finished;
  }

  reloadAnimationEffect() {
    if (this.scrollAnimation == "desktop_only" && window.innerWidth < 768) {
      return;
    }
    Motion.animate(
      this.visibleItems,
      {
        y: [30, 0],
        opacity: [0, 1],
        visibility: ["hidden", "visible"],
      },
      {
        duration: 0.5,
        delay: Motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      },
    ).finished;
  }
}
customElements.define("motion-items-effect", MotionItemsEffect);

class ButtonCopyLink extends HTMLButtonElement {
  constructor() {
    super();
    this.boundClick = this.onClick.bind(this);
    this.init();
  }
  init() {
    this.addEventListener("click", this.boundClick, false);
  }
  onClick() {
    const url = this.getAttribute("data-href");
    navigator.clipboard.writeText(url);
    notifier.show(window.message.socialCopyLink.success, "success");
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundClick);
  }
}
customElements.define("button-copy-link", ButtonCopyLink, {
  extends: "button",
});
CustomElement.observeAndPatchCustomElements({
  "button-copy-link": {
    tagElement: "button",
    classElement: ButtonCopyLink,
  },
});

class ButtonQuickView extends HTMLButtonElement {
  constructor() {
    super();
    this.boundClick = this.onClick.bind(this);
    this.boundKeypress = this.handleKeypress.bind(this);
    this.boundSectionSelect = this.handleSectionSelect.bind(this);
    this.boundSectionDeselect = this.handleSectionDeselect.bind(this);
    this.requestId = null;
    this.abortController = null;
    this.init();
  }

  init() {
    this.addEventListener("click", this.boundClick, false);
    this.addEventListener("keypress", this.boundKeypress, false);
    window.Shopify.designMode &&
      (document.addEventListener(
        "shopify:section:select",
        this.boundSectionSelect,
      ),
      document.addEventListener(
        "shopify:section:deselect",
        this.boundSectionDeselect,
      ));
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundClick);
    this.removeEventListener("keypress", this.boundKeypress);
    document.removeEventListener(
      "shopify:section:select",
      this.boundSectionSelect,
    );
    document.removeEventListener(
      "shopify:section:deselect",
      this.boundSectionDeselect,
    );
    this.abortFetch();
    this.setLoading(false);
  }

  handleKeypress(event) {
    if (event.key === "Enter") {
      this.onClick(event);
    }
  }

  handleSectionSelect(event) {
    if (!this.isConnected) return;
    const currentTarget = event.target;
    const sectionData = safeParseJSON(
      currentTarget?.dataset?.shopifyEditorSection,
    );
    if (sectionData?.id === this.sectionId) {
      const drawer = document.querySelector("quickview-drawer");
      if (drawer) {
        eventModal(drawer, "open", false, "delay", true);
      }
    }
  }

  handleSectionDeselect() {
    const drawer = document.querySelector("quickview-drawer");
    if (drawer) {
      eventModal(drawer, "close", false);
    }
  }

  get drawer() {
    return document.querySelector("quickview-drawer");
  }

  get sectionId() {
    return this.drawer?.dataset.sectionId || null;
  }

  async onClick(e) {
    e.preventDefault();

    if (!this.dataset.url) return;

    this.setLoading(true);

    if (!this.sectionId) {
      window.location.href = this.dataset.url;
      return;
    }

    if (this.classList.contains("active")) {
      this.openDrawer();
      this.setLoading(false);
      return;
    }

    const requestId = this.startFetch();

    await Promise.all([
      import(importJs.mediaGallery),
      import(importJs.productModel),
    ]);

    if (!this.isCurrentRequest(requestId)) return;

    this.clearActive();
    await this.fetchContent(requestId);
    if (!this.isConnected) return;
    const drawer = this.drawer;
    if (drawer) {
      drawer.querySelector(".drawer__body")?.scrollTo(0, 0);
      drawer.querySelector(".product-detail__information")?.scrollTo(0, 0);
    }

    if (this.isConnected) {
      this.setLoading(false);
    }
  }

  startFetch() {
    ButtonQuickView.abortActiveFetch();
    this.abortController =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    this.requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    ButtonQuickView.activeButton = this;
    ButtonQuickView.activeRequestId = this.requestId;
    ButtonQuickView.activeController = this.abortController;
    return this.requestId;
  }

  abortFetch() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (ButtonQuickView.activeButton === this) {
      ButtonQuickView.activeButton = null;
      ButtonQuickView.activeRequestId = null;
      ButtonQuickView.activeController = null;
    }
    this.requestId = null;
  }

  isCurrentRequest(requestId) {
    return (
      this.isConnected &&
      requestId &&
      ButtonQuickView.activeButton === this &&
      ButtonQuickView.activeRequestId === requestId
    );
  }

  static abortActiveFetch() {
    if (ButtonQuickView.activeController) {
      ButtonQuickView.activeController.abort();
    }
    if (ButtonQuickView.activeButton) {
      ButtonQuickView.activeButton.abortController = null;
      ButtonQuickView.activeButton.requestId = null;
      ButtonQuickView.activeButton.setLoading(false);
    }
    ButtonQuickView.activeButton = null;
    ButtonQuickView.activeRequestId = null;
    ButtonQuickView.activeController = null;
  }

  async fetchContent(requestId) {
    try {
      const res = await fetch(
        `${this.dataset.url}?section_id=${this.sectionId}`,
        this.abortController ? { signal: this.abortController.signal } : undefined,
      );
      const text = await res.text();

      const html = NextSkyTheme.parser.parseFromString(text, "text/html");

      if (!this.isCurrentRequest(requestId)) return;

      const targetProduct = document.querySelector(".quickview-product");
      const sourceProduct = html.querySelector(".quickview-product");
      if (!targetProduct || !sourceProduct) return;

      targetProduct.innerHTML = sourceProduct.innerHTML;

      await Promise.all([
        import(importJs.mediaLightboxGallery),
        import(importJs.countdownTimer),
        import(importJs.recipientForm),
      ]);

      new LazyLoader(".image-lazy-load");

      if (!this.isCurrentRequest(requestId)) return;

      this.classList.add("active");
      this.openDrawer();
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error(err);
    } finally {
      if (this.isCurrentRequest(requestId)) {
        this.abortFetch();
      }
    }
  }

  openDrawer() {
    const drawer = this.drawer;
    if (!drawer) return;

    this.handleBeforeYouLeave();

    eventModal(drawer, "open", false, "delay", true);
    drawer.querySelector(".modal-inner")?.scrollTo(0, 0);

    NextSkyTheme.global.rootToFocus = this;
  }

  handleBeforeYouLeave() {
    const beforeYouLeave = document.querySelector("before-you-leave.active");

    if (beforeYouLeave) {
      eventModal(beforeYouLeave, "close", false);

      NextSkyTheme.setCookie?.("before_you_leave_hidden", "true", 1);
    }
  }

  clearActive() {
    document
      .querySelectorAll(".button-quick-view.active")
      .forEach((el) => el.classList.remove("active"));
  }

  setLoading(state) {
    this.classList.toggle("loading", state);
    this.toggleAttribute("aria-disabled", state);
  }
}

customElements.define("button-quick-view", ButtonQuickView, {
  extends: "button",
});
CustomElement.observeAndPatchCustomElements({
  "button-quick-view": {
    tagElement: "button",
    classElement: ButtonQuickView,
  },
});

class QuickAdd extends HTMLElement {
  constructor() {
    super();
    this.btnQuickAdd = this.querySelector(".btn__quick-add-action");
    this.btnClose = this.querySelector(".button__close");
    this.variantQuickAdd = this.querySelector("variant-quick-add");
    this.popupTimer = null;
    this.currentPopup = null;
    this.boundQuickAddClick = this.handleQuickAddClick.bind(this);
    this.boundCloseClick = this.handleCloseClick.bind(this);
    this.init();
  }

  init() {
    if (this.btnQuickAdd) {
      this.btnQuickAdd.addEventListener("click", this.boundQuickAddClick);
    }

    if (this.btnClose) {
      this.btnClose.addEventListener("click", this.boundCloseClick);
    }
  }

  disconnectedCallback() {
    this.btnQuickAdd?.removeEventListener("click", this.boundQuickAddClick);
    this.btnClose?.removeEventListener("click", this.boundCloseClick);
    if (this.popupTimer) {
      clearTimeout(this.popupTimer);
      this.popupTimer = null;
    }
    this.removePendingPopup();
    this.currentPopup = null;
  }

  handleQuickAddClick(e) {
    e.preventDefault();
    const width = window.innerWidth;
    if (width > 767) {
      this.classList.add("active");
    } else {
      this.initPopup();
    }
  }

  handleCloseClick(e) {
    e.preventDefault();
    this.classList.remove("active");
  }

  initPopup() {
    if (this.popupTimer) {
      clearTimeout(this.popupTimer);
      this.popupTimer = null;
    }

    this.removePendingPopup();
    const template = this.querySelector("template.quick-add-popup");
    if (template) {
      const content = document.createElement("div");
      content.appendChild(template.content.firstElementChild.cloneNode(true));
      const popup = content.querySelector("quick-add-popup");
      if (popup) {
        NextSkyTheme.getBody().appendChild(popup);
        this.currentPopup = popup;
        this.popupTimer = setTimeout(() => {
          this.popupTimer = null;
          if (this.isConnected && popup.isConnected) {
            eventModal(popup, "open", true);
          }
        }, 100);
      }
    }
    NextSkyTheme.global.rootToFocus = this.btnQuickAdd;
  }

  removePendingPopup() {
    if (!this.currentPopup || this.currentPopup.classList.contains("active")) {
      return;
    }

    this.currentPopup.remove();
    this.currentPopup = null;
  }
}
customElements.define("quick-add", QuickAdd);

class SelectContact extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    const text = this.getAttribute("text");
    if (text) {
      const options = text.split(",").map((item) => item.trim());
      const selectElement = this.closest(
        ".field-contact-subject",
      ).querySelector("select");
      options.forEach((optionText) => {
        const option = document.createElement("option");
        option.value = optionText.toLowerCase();
        option.textContent =
          optionText.charAt(0).toUpperCase() + optionText.slice(1);
        selectElement.appendChild(option);
      });
    }
  }
}
customElements.define("select-contact", SelectContact);

class BeforeYouLeave extends HTMLElement {
  constructor() {
    super();
    this.delay = parseInt(this.dataset.timeDelay, 10);
    this.sectionId = this.dataset.sectionId;
    this.interactions = 0;
    this.popupActive = false;
    this.cookieName = "before_you_leave_hidden";
    this.closeHandler = this.closeHandler.bind(this);
    this.initTimer = null;
    this._inactivityTimer = null;
    this._inactivityEvents = ["scroll", "click", "mousemove", "keydown"];
    this.boundResetTimer = this.resetInactivityTimer.bind(this);
    this.boundSectionSelect = this.handleSectionSelect.bind(this);
    this.boundSectionDeselect = this.handleSectionDeselect.bind(this);
    this.couponCleanups = [];
    this.copyTimers = [];
    if (
      NextSkyTheme.getCookie &&
      NextSkyTheme.getCookie(this.cookieName) === "true"
    ) {
      return;
    }
    if (!window.Shopify.designMode) {
      this.initTimer = setTimeout(() => {
        this.initTimer = null;
        if (this.isConnected) this.init();
      }, 100);
    }
    window.Shopify.designMode &&
      (document.addEventListener("shopify:section:select", this.boundSectionSelect),
      document.addEventListener("shopify:section:deselect", this.boundSectionDeselect));
  }

  disconnectedCallback() {
    if (this.initTimer) {
      clearTimeout(this.initTimer);
      this.initTimer = null;
    }
    if (this._inactivityTimer) {
      clearTimeout(this._inactivityTimer);
      this._inactivityTimer = null;
    }
    this._inactivityEvents.forEach((event) => {
      window.removeEventListener(event, this.boundResetTimer);
    });
    document.removeEventListener("shopify:section:select", this.boundSectionSelect);
    document.removeEventListener("shopify:section:deselect", this.boundSectionDeselect);
    this.couponCleanups.forEach((cleanup) => cleanup());
    this.couponCleanups = [];
    this.copyTimers.forEach((timerId) => clearTimeout(timerId));
    this.copyTimers = [];
  }

  init() {
    if (
      NextSkyTheme.getCookie &&
      NextSkyTheme.getCookie(this.cookieName) === "true"
    ) {
      return;
    }
    const sourceContent = document.getElementById(this.sectionId);
    if (!sourceContent) {
      return;
    }
    this.innerHTML = sourceContent.innerHTML;
    if (typeof BlsLazyloadImg !== "undefined") {
      BlsLazyloadImg.init();
    }
    this.initPopup();
  }

  handleSectionSelect(event) {
    const currentTarget = event.target;
    const sectionData = safeParseJSON(
      currentTarget?.dataset?.shopifyEditorSection,
    );
    if (sectionData?.id === this.sectionId) {
      eventModal(this, "open", false);
      this.bindCouponCopy();
    }
  }

  handleSectionDeselect() {
    const modal = document.querySelector("before-you-leave");
    if (modal) {
      eventModal(modal, "close", false);
    }
  }

  initPopup() {
    if (
      NextSkyTheme.getCookie &&
      NextSkyTheme.getCookie(this.cookieName) === "true"
    ) {
      return;
    }
    if (this._inactivityInitialized) return;
    this._inactivityInitialized = true;
    this._inactivityTriggered = false;
    this._inactivityEvents.forEach((event) => {
      window.addEventListener(event, this.boundResetTimer, { passive: true });
    });
    this.resetInactivityTimer();
  }

  resetInactivityTimer() {
    if (this._inactivityTimer) clearTimeout(this._inactivityTimer);
    if (this._inactivityTriggered) return;
    this._inactivityTimer = setTimeout(() => {
      if (!this.isConnected || this._inactivityTriggered) return;
      this._inactivityTriggered = true;
      this._inactivityTimer = null;
      const activeModal =
        NextSkyTheme.getRoot().classList.contains("open-modal");
      if (!activeModal) {
        eventModal(this, "open", true);
        this.bindCouponCopy();
      } else {
        this._inactivityTriggered = false;
        this.resetInactivityTimer();
      }
      this._inactivityEvents.forEach((event) => {
        window.removeEventListener(event, this.boundResetTimer);
      });
    }, this.delay * 1000);
  }

  closeHandler(e) {
    if (
      e.target.closest(".close-before") ||
      e.target.matches(".modal-overlay")
    ) {
      eventModal(this, "close");
      this.popupActive = false;
      if (NextSkyTheme.setCookie) {
        NextSkyTheme.setCookie(this.cookieName, "true", 1);
      }
    }
  }

  bindCouponCopy() {
    this.couponCleanups.forEach((cleanup) => cleanup());
    this.couponCleanups = [];
    const discounts = this.querySelectorAll(".discount");
    discounts.forEach((el) => {
      const handleCouponClick = (e) => {
        e.preventDefault();
        navigator.clipboard.writeText(el.dataset.code);
        el.classList.add("action-copy");
        const timerId = setTimeout(() => {
          this.copyTimers = this.copyTimers.filter((id) => id !== timerId);
          if (this.isConnected) el.classList.remove("action-copy");
        }, 2000);
        this.copyTimers.push(timerId);
      };
      el.addEventListener("click", handleCouponClick);
      this.couponCleanups.push(() => el.removeEventListener("click", handleCouponClick));
    });
  }
}

customElements.define("before-you-leave", BeforeYouLeave);

class ShareButton extends HTMLElement {
  constructor() {
    super();
    this.boundHandleShare = this.handleShare.bind(this);
    this.addEventListener("click", this.boundHandleShare);
  }

  handleShare() {
    const title = this.getAttribute("data-title") || document.title;
    const text = this.getAttribute("data-text") || "";
    const url = this.getAttribute("data-url") || window.location.href;

    if (navigator.share) {
      navigator.share({ title, text, url });
    }
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.boundHandleShare);
  }
}
customElements.define("share-button", ShareButton);

class InnerTab extends HTMLElement {
  constructor() {
    super();
    this.boundButtonClick = this.onButtonClick.bind(this);
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.boundButtonClick),
    );
  }

  disconnectedCallback() {
    this.querySelectorAll("button").forEach((button) =>
      button.removeEventListener("click", this.boundButtonClick),
    );
  }

  onButtonClick(event) {
    const currentTarget = event.currentTarget;
    if (currentTarget.classList.contains("active")) {
      return;
    }
    const id = currentTarget.getAttribute("data-id");
    const visibleItems = document.getElementById(id);
    this.querySelectorAll("button").forEach((button) =>
      button.classList.remove("active"),
    );
    currentTarget.classList.add("active");
    this.closest(".content-tabs")
      .querySelectorAll(".tab-content")
      .forEach((result) => {
        if (result.id !== id) {
          result.classList.add("hidden");
          Motion.animate(
            result,
            {
              transform: "translateY(10px)",
              opacity: 0,
              visibility: "hidden",
            },
            {
              duration: 0,
            },
          );
        }
      });

    visibleItems.classList.remove("hidden");
    Motion.animate(
      visibleItems,
      {
        transform: ["translateY(10px)", "translateY(0)"],
        opacity: [0, 1],
        visibility: ["hidden", "visible"],
      },
      {
        duration: 0.5,
        delay: Motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      },
    );
  }
}
customElements.define("inner-tab", InnerTab);

class ProgressBarload extends HTMLElement {
  constructor() {
    super();
    const container = document.createElement("div");
    container.className = "progress-load-page";
    const bar = document.createElement("div");
    bar.className = "indeterminate";
    bar.style.width = "0%";
    container.appendChild(bar);
    this.appendChild(container);
    this.indicator = bar;
    this.progress = 0;
    this.minAnimationTime = 1000;
    this.boundComplete = this.complete.bind(this);
  }

  connectedCallback() {
    window.addEventListener("beforeunload", this.boundComplete);
  }

  disconnectedCallback() {
    window.removeEventListener("beforeunload", this.boundComplete);
  }

  complete() {
    this.style.display = "flex";
    this.progress = 0;
    this.isAnimating = true;
    this.indicator.style.transition = "none";
    this.indicator.style.width = "0%";
    this.indicator.style.opacity = "1";
    this.indicator.offsetWidth;
    this.indicator.style.transition = `width ${this.minAnimationTime}ms ease`;
    this.indicator.style.width = "100%";
  }
}
customElements.define("progress-bar-load", ProgressBarload);

class ProgressBarBlock extends HTMLElement {
  constructor() {
    super();
    this.percentageElement = this.querySelector(".progress-percentage");
    this.progressBar = this.querySelector(".progress-bar");
    this.targetPercentage = this.percentageElement
      ? parseInt(this.percentageElement.dataset.percentage)
      : 0;
    this.timeoutIds = [];
  }

  connectedCallback() {
    if (this.percentageElement) {
      this.animatePercentage();
    }
  }

  animatePercentage() {
    let currentPercentage = 0;
    const updatePercentage = () => {
      if (!this.isConnected || !this.percentageElement) return;
      if (currentPercentage <= this.targetPercentage) {
        this.percentageElement.textContent = currentPercentage + "%";
        currentPercentage++;
        this.setManagedTimeout(updatePercentage, 20);
      } else {
        this.percentageElement.textContent = this.targetPercentage + "%";
      }
    };
    this.setManagedTimeout(updatePercentage, 100);
  }

  resetAndAnimate() {
    if (this.percentageElement && this.progressBar) {
      this.progressBar.style.width = "0";
      this.progressBar.style.animation = "none";
      this.percentageElement.textContent = "0%";

      this.setManagedTimeout(() => {
        if (!this.isConnected || !this.progressBar) return;
        this.progressBar.style.animation = `progressAnimation ${
          this.targetPercentage * 3
        }s ease-in-out forwards`;
        this.animatePercentage();
      }, 10);
    }
  }

  setManagedTimeout(callback, delay) {
    const timeoutId = setTimeout(() => {
      this.timeoutIds = this.timeoutIds.filter((id) => id !== timeoutId);
      callback();
    }, delay);
    this.timeoutIds.push(timeoutId);
  }

  disconnectedCallback() {
    this.timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeoutIds = [];
  }
}

customElements.define("progress-bar-block", ProgressBarBlock);

class CounterAnimation extends HTMLElement {
  constructor() {
    super();
    this.intersectionObserver = null;
    this.resizeObserver = null;
    this.animationFrame = 0;
  }

  connectedCallback() {
    this.counterElement = this.querySelector("[data-counter-target]");
    this.circleElement = this.querySelector(".progress-ring__circle");
    this.bgCircleElement = this.querySelector(".progress-ring__bg");
    this.svgElement = this.querySelector(".progress-ring");
    this.targetValue = parseFloat(this.dataset.counter) || 100;
    this.startValue = parseFloat(this.dataset.start) || 0;
    this.duration = parseFloat(this.dataset.duration) || 2000;
    this.isAnimated = false;

    this.setCircleAttributes();

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!this.isConnected) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resetAnimation();
            this.animateCounter();
          }
        });
      },
      { threshold: 0.5 },
    );
    this.intersectionObserver.observe(this);

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isConnected) return;
      this.setCircleAttributes();
      if (this.isAnimated && this.circleElement && this.circumference) {
        const percent = Math.min(this.targetValue, 100) / 100;
        const offset = this.circumference - percent * this.circumference;
        this.circleElement.style.strokeDashoffset = offset;
      }
      this.updateTextPosition();
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
    }
  }

  setCircleAttributes() {
    const styles = getComputedStyle(this);
    const size = parseFloat(styles.getPropertyValue("--circle-size")) || 150;
    const stroke = parseFloat(styles.getPropertyValue("--circle-stroke")) || 8;
    const radius = size / 2 - stroke / 2;
    const center = size / 2;

    if (this.svgElement) {
      this.svgElement.setAttribute("viewBox", `0 0 ${size} ${size}`);
    }

    [this.circleElement, this.bgCircleElement].forEach((el) => {
      if (el) {
        el.setAttribute("r", radius);
        el.setAttribute("cx", center);
        el.setAttribute("cy", center);
      }
    });

    this.radius = radius;
    this.circumference = 2 * Math.PI * radius;

    if (this.circleElement) {
      this.circleElement.style.strokeDasharray = `${this.circumference} ${this.circumference}`;
      this.circleElement.style.strokeDashoffset = this.circumference;
    }

    this.updateTextPosition();
  }

  updateTextPosition() {
    if (this.counterElement && this.circleElement) {
      const centerX = parseFloat(this.circleElement.getAttribute("cx"));
      const centerY = parseFloat(this.circleElement.getAttribute("cy"));
      this.counterElement.setAttribute("x", centerX);
      this.counterElement.setAttribute("y", centerY);
      this.counterElement.setAttribute("dominant-baseline", "middle");
      this.counterElement.removeAttribute("dy");
    }
  }

  resetAnimation() {
    this.isAnimated = false;
    if (!this.counterElement) return;
    this.counterElement.textContent = this.startValue;
    if (this.circleElement && this.circumference) {
      this.circleElement.style.strokeDashoffset = this.circumference;
    }
  }

  animateCounter() {
    if (this.isAnimated) return;

    let current = this.startValue;
    const range = this.targetValue - this.startValue;
    const increment = range / (this.duration / 16);

    const updateCounter = () => {
      this.animationFrame = 0;
      if (!this.isConnected || !this.counterElement) return;
      current += increment;
      if (current >= this.targetValue) {
        current = this.targetValue;
        this.isAnimated = true;
      }

      this.counterElement.textContent = Math.ceil(current);
      const progressPercent = Math.min(current, 100);
      this.style.setProperty("--progress-desktop", `${progressPercent}%`);

      if (this.circleElement && this.circumference) {
        const percent = Math.min(current, 100) / 100;
        const offset = this.circumference - percent * this.circumference;
        this.circleElement.style.strokeDashoffset = offset;
      }

      if (current < this.targetValue) {
        this.animationFrame = requestAnimationFrame(updateCounter);
      }
    };

    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.animationFrame = requestAnimationFrame(updateCounter);
  }
}
customElements.define("counter-animation", CounterAnimation);

class TabRow extends HTMLElement {
  constructor() {
    super();
    this.tabHeadings = this.querySelectorAll(".tab-heading");
    this.contentDisplay = this.querySelector("#tab-content-display");
    this.cleanupHandlers = [];

    this.activeIndex = Array.from(this.tabHeadings).findIndex((heading) =>
      heading.classList.contains("active"),
    );
    if (this.activeIndex === -1) {
      this.activeIndex = 0;
      this.tabHeadings[0]?.classList.add("active");
    }

    if (!this.contentDisplay) {
      console.error(
        "Content display element with ID 'tab-content-display' not found within <tab-row>.",
      );
      return;
    }

    this.init();
  }

  init() {
    this.tabHeadings.forEach((heading, index) => {
      const clickHandler = () => this.switchTab(index);
      const keydownHandler = (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.switchTab(index);
        }
      };

      heading.addEventListener("click", clickHandler);
      heading.addEventListener("keydown", keydownHandler);
      this.cleanupHandlers.push(() => {
        heading.removeEventListener("click", clickHandler);
        heading.removeEventListener("keydown", keydownHandler);
      });
    });

    if (this.tabHeadings.length > 0) {
      this._displayNewContent(
        this.activeIndex,
        this.tabHeadings[this.activeIndex],
        false,
      );
    }
  }

  _getTemplateContent(templateId) {
    const template = document.getElementById(templateId);
    if (template && template.content) {
      return template.content.cloneNode(true);
    }
    return null;
  }

  switchTab(newIndex) {
    const oldHeading = this.tabHeadings[this.activeIndex];
    const newHeading = this.tabHeadings[newIndex];

    if (newIndex === this.activeIndex || !newHeading) return;

    if (oldHeading) {
      oldHeading.classList.remove("active");
      oldHeading.setAttribute("aria-selected", "false");
    }

    newHeading.classList.add("active");
    newHeading.setAttribute("aria-selected", "true");
    newHeading.focus();

    const oldContentWrapper = this.contentDisplay.firstElementChild;

    if (oldContentWrapper && typeof Motion !== "undefined") {
      Motion.animate(
        oldContentWrapper,
        {
          transform: "translateY(10px)",
          opacity: 0,
        },
        {
          duration: 0.2,
          easing: [0, 0, 0.3, 1],
        },
      ).finished.then(() => {
        if (!this.isConnected || !this.contentDisplay) return;
        this.contentDisplay.innerHTML = "";
        this._displayNewContent(newIndex, newHeading, true);
      });
    } else {
      this.contentDisplay.innerHTML = "";
      this._displayNewContent(newIndex, newHeading, true);
    }
  }

  _displayNewContent(newIndex, newHeading, useAnimation = true) {
    if (!this.isConnected || !this.contentDisplay || !newHeading) return;
    const templateId = newHeading.dataset.targetContentId;
    const newContent = this._getTemplateContent(templateId);

    this.activeIndex = newIndex;

    if (newContent) {
      this.contentDisplay.appendChild(newContent);

      if (useAnimation && typeof Motion !== "undefined") {
        Motion.animate(
          newContent,
          {
            transform: ["translateY(10px)", "translateY(0)"],
            opacity: [0, 1],
          },
          {
            duration: 0.2,
            delay: 0,
            easing: [0, 0, 0.3, 1],
          },
        );
      }
    }
  }

  disconnectedCallback() {
    this.cleanupHandlers.forEach((cleanup) => cleanup());
    this.cleanupHandlers = [];
  }
}

customElements.define("tab-row", TabRow);

class DiscountPopup extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.modal = this.querySelector("discount-modal-popup");
    this.boundSectionSelect = this.handleSectionSelect.bind(this);
    this.boundSectionDeselect = this.handleSectionDeselect.bind(this);
    window.Shopify.designMode &&
      (document.addEventListener(
        "shopify:section:select",
        this.boundSectionSelect,
      ),
      document.addEventListener(
        "shopify:section:deselect",
        this.boundSectionDeselect,
      ));
  }

  disconnectedCallback() {
    document.removeEventListener(
      "shopify:section:select",
      this.boundSectionSelect,
    );
    document.removeEventListener(
      "shopify:section:deselect",
      this.boundSectionDeselect,
    );
  }

  handleSectionSelect(event) {
    const currentTarget = event.target;
    const sectionData = safeParseJSON(
      currentTarget?.dataset?.shopifyEditorSection,
    );
    if (sectionData?.id === this.sectionId && this.modal) {
      eventModal(this.modal, "open", false);
    }
  }

  handleSectionDeselect() {
    if (this.modal) {
      eventModal(this.modal, "close", false);
    }
  }
}
customElements.define("discount-popup", DiscountPopup);

class LiveView extends HTMLElement {
  constructor() {
    super();
    this.min = 0;
    this.max = 0;
    this.interval = 0;
    this.message = "";
    this.liveViewInterval = null;
    this.initialized = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.min = parseInt(this.dataset.min, 10);
    this.max = parseInt(this.dataset.max, 10);
    this.interval = parseInt(this.dataset.interval, 10);
    this.message = this.dataset.message || "";

    if (
      !this.message ||
      !Number.isFinite(this.min) ||
      !Number.isFinite(this.max) ||
      !Number.isFinite(this.interval) ||
      this.interval <= 0
    ) {
      return;
    }

    if (this.min > this.max) {
      [this.min, this.max] = [this.max, this.min];
    }

    this.handle();
    this.liveViewInterval = setInterval(() => {
      if (!this.isConnected) return;
      this.handle();
    }, this.interval * 1000);
  }

  handle() {
    const count = this.randomSelect(this.min, this.max);
    const parts = String(this.message).split("[count]");
    this.replaceChildren();
    parts.forEach((part, index) => {
      if (part) this.appendChild(document.createTextNode(part));
      if (index < parts.length - 1) {
        const countElement = document.createElement("span");
        countElement.className = "heading-style";
        countElement.textContent = count;
        this.appendChild(countElement);
      }
    });
  }

  randomSelect(min, max) {
    const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
    return randomNumber;
  }

  disconnectedCallback() {
    if (this.liveViewInterval) {
      clearInterval(this.liveViewInterval);
      this.liveViewInterval = null;
    }
    this.initialized = false;
  }
}
customElements.define("live-view", LiveView);

// Countdown timer for campaign sale banners
function initSaleCountdown(cd) {
  if (!cd) return;
  var endStr = cd.getAttribute('data-end');
  if (!endStr) return;
  var endDate = Date.parse(endStr);
  if (isNaN(endDate)) {
    var parts = endStr.split(/[- :]/);
    endDate = new Date(+parts[0], +parts[1] - 1, +parts[2], +parts[3] || 0, +parts[4] || 0, +parts[5] || 0).getTime();
  }
  if (isNaN(endDate)) return;

  clearSaleCountdown(cd);

  var daysEl = cd.querySelector('[data-days]');
  var hoursEl = cd.querySelector('[data-hours]');
  var minsEl = cd.querySelector('[data-mins]');
  var secsEl = cd.querySelector('[data-secs]');

  function pad(n) { return n.toString().padStart(2, '0'); }
  function tick() {
    var diff = endDate - Date.now();
    if (diff <= 0) {
      if (daysEl) daysEl.textContent = '00';
      if (hoursEl) hoursEl.textContent = '00';
      if (minsEl) minsEl.textContent = '00';
      if (secsEl) secsEl.textContent = '00';
      if (cd._cdTimer) { clearInterval(cd._cdTimer); cd._cdTimer = null; }
      return;
    }
    if (daysEl) daysEl.textContent = pad(Math.floor(diff / 86400000));
    if (hoursEl) hoursEl.textContent = pad(Math.floor((diff % 86400000) / 3600000));
    if (minsEl) minsEl.textContent = pad(Math.floor((diff % 3600000) / 60000));
    if (secsEl) secsEl.textContent = pad(Math.floor((diff % 60000) / 1000));
  }
  tick();
  cd._cdTimer = setInterval(tick, 1000);
}

function clearSaleCountdown(cd) {
  if (!cd || !cd._cdTimer) return;
  clearInterval(cd._cdTimer);
  delete cd._cdTimer;
}

function clearAllSaleCountdowns() {
  document.querySelectorAll('[data-countdown]').forEach(clearSaleCountdown);
}

// Init on page load
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('[data-countdown]').forEach(initSaleCountdown);
});

// Init on editor section load/re-render
document.addEventListener('shopify:section:load', function (e) {
  e.target.querySelectorAll('[data-countdown]').forEach(initSaleCountdown);
});

// Cleanup on section unload
document.addEventListener('shopify:section:unload', function (e) {
  e.target.querySelectorAll('[data-countdown]').forEach(clearSaleCountdown);
});

window.addEventListener('pagehide', clearAllSaleCountdowns, { once: true });

// Global copy function for m-sale-coupon; onclick attribute survives innerHTML.
(function () {
  var copyTimers = new WeakMap();

  window.MC = function (btn) {
    var text = btn && btn.getAttribute('data-code');
    if (!btn || !text) return;

    var activeTimer = copyTimers.get(btn);
    if (activeTimer) {
      clearTimeout(activeTimer);
      copyTimers.delete(btn);
    }

    // 1) Select visible code text so user can Ctrl+C.
    var wrap = btn.closest('.mc-copy-wrap');
    var codeEl = wrap && wrap.querySelector('.mc-code');
    if (codeEl) {
      var sel = window.getSelection();
      var range = document.createRange();
      range.selectNodeContents(codeEl);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    // 2) Try execCommand as a best-effort legacy clipboard fallback.
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);

    // 3) Always show copied feedback; text is at least selected.
    btn.classList.add('is-copied');
    var timerId = setTimeout(function () {
      copyTimers.delete(btn);
      if (btn.isConnected) btn.classList.remove('is-copied');
    }, 2000);
    copyTimers.set(btn, timerId);
  };
})();

/* Responsive metafield images can inherit a stale browser failure for a versioned
 * Shopify CDN URL. Hide the browser's broken-image chrome, retry once with a new
 * cache key, and leave the module's governed background visible on final failure. */
(function () {
  document.documentElement.classList.add('bbr-remote-images-ready');

  function markRemoteImageLoaded(img) {
    img.classList.remove('is-error', 'is-retrying');
    img.classList.add('is-loaded');
  }

  function markRemoteImageFailed(img) {
    img.classList.remove('is-loaded', 'is-retrying');
    img.classList.add('is-error');
  }

  function retryRemoteImage(img) {
    if (img.dataset.bbrRemoteImageRetried === 'true') {
      markRemoteImageFailed(img);
      return;
    }

    img.dataset.bbrRemoteImageRetried = 'true';
    img.classList.add('is-retrying');
    var retryUrl = img.currentSrc || img.src;
    if (!retryUrl) {
      markRemoteImageFailed(img);
      return;
    }

    img.removeAttribute('srcset');
    var separator = retryUrl.indexOf('?') === -1 ? '?' : '&';
    img.src = retryUrl + separator + '_bbr_image_retry=' + Date.now();
  }

  function bindRemoteImage(img) {
    if (!img || img.dataset.bbrRemoteImageBound === 'true') return;
    img.dataset.bbrRemoteImageBound = 'true';

    img._bbrRemoteImageLoad = function () {
      markRemoteImageLoaded(img);
    };
    img._bbrRemoteImageError = function () {
      retryRemoteImage(img);
    };
    img.addEventListener('load', img._bbrRemoteImageLoad);
    img.addEventListener('error', img._bbrRemoteImageError);

    if (img.complete) {
      if (img.naturalWidth > 0) markRemoteImageLoaded(img);
      else retryRemoteImage(img);
    }
  }

  function setupRemoteImages(root) {
    (root || document).querySelectorAll('img[data-bbr-remote-image]').forEach(bindRemoteImage);
  }

  function cleanupRemoteImages(root) {
    (root || document).querySelectorAll('img[data-bbr-remote-image]').forEach(function (img) {
      if (img._bbrRemoteImageLoad) img.removeEventListener('load', img._bbrRemoteImageLoad);
      if (img._bbrRemoteImageError) img.removeEventListener('error', img._bbrRemoteImageError);
      delete img._bbrRemoteImageLoad;
      delete img._bbrRemoteImageError;
      delete img.dataset.bbrRemoteImageBound;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setupRemoteImages(document);
    }, { once: true });
  } else {
    setupRemoteImages(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    setupRemoteImages(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    cleanupRemoteImages(event.target);
  });
})();

/* Assurance carousel navigation lives at the theme layer so deferred section
 * lifecycle events cannot leave visible controls without click behavior. */
(function () {
  if (window.__babonirAssuranceNavigationBound) return;
  window.__babonirAssuranceNavigationBound = true;
  document.addEventListener('click', function (event) {
    var button = event.target.closest('.assur-section .assur-arrow');
    if (!button) return;
    var section = button.closest('.assur-section');
    var track = section && section.querySelector('.assur-scroll');
    var items = track ? Array.from(track.querySelectorAll('.assur-item:not([aria-hidden])')) : [];
    if (!track || !items.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var current = items.reduce(function (best, item, index) {
      return Math.abs(item.offsetLeft - track.scrollLeft) < Math.abs(items[best].offsetLeft - track.scrollLeft) ? index : best;
    }, 0);
    var delta = button.classList.contains('next') ? 1 : -1;
    var targetIndex = (current + delta + items.length) % items.length;
    track.scrollTo({ left: items[targetIndex].offsetLeft, behavior: 'auto' });
    section.querySelectorAll('.assur-dot').forEach(function (dot, index) {
      dot.classList.toggle('active', index === targetIndex);
      if (index === targetIndex) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }, true);
})();

(() => {
  function hydrateBeforeLeaveBanner(root) {
    (root || document)
      .querySelectorAll('before-you-leave .overlay-bg img[data-srcset]')
      .forEach((img) => {
        const srcset = (img.getAttribute('data-srcset') || '').replace(/,\s*$/, '');
        if (!srcset) return;
        img.setAttribute('srcset', srcset);
        img.removeAttribute('data-srcset');
        img.setAttribute('loading', 'eager');
        img.setAttribute('fetchpriority', 'high');
        img.classList.remove('image-lazy-load');
        img.classList.add('lazy-loaded');
      });
  }

  document.addEventListener('DOMContentLoaded', () => hydrateBeforeLeaveBanner(document));
  document.addEventListener('shopify:section:load', (event) => hydrateBeforeLeaveBanner(event.target));
  document.addEventListener('pointerout', (event) => {
    if (event.clientY <= 0 || !event.relatedTarget) {
      hydrateBeforeLeaveBanner(document);
    }
  });

  if (document.body) {
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) hydrateBeforeLeaveBanner(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

// Banner click navigation — skips copy button area
window.bannerClick = function (e) {
  if (e.target.closest('.mc-copy-wrap')) return;
  var url = e.currentTarget.getAttribute('data-url');
  if (url) window.location.href = url;
};

// BABONIR product finder controls for custom homepage products.
(function () {
  var finderInitTimeout = null;

  function createControls() {
    var wrap = document.createElement('div');
    wrap.className = 'bbr-finder-controls';
    wrap.setAttribute('aria-label', 'Product carousel controls');
    wrap.hidden = true;
    wrap.innerHTML = [
      '<button class="bbr-finder-arrow" type="button" data-bbr-finder-prev aria-label="Previous products"><svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg></button>',
      '<div class="bbr-finder-dots" aria-hidden="true"></div>',
      '<button class="bbr-finder-arrow" type="button" data-bbr-finder-next aria-label="Next products"><svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg></button>'
    ].join('');
    return wrap;
  }

  function updateFinderControls(slider) {
    var controls = slider.querySelector('.bbr-finder-controls');
    var swiper = slider.swiper;
    if (!controls || !swiper) return;

    var steps = swiper.snapGrid && swiper.snapGrid.length ? swiper.snapGrid.length : 1;
    var index = typeof swiper.snapIndex === 'number' ? swiper.snapIndex : swiper.activeIndex || 0;
    var dots = controls.querySelector('.bbr-finder-dots');
    var prev = controls.querySelector('[data-bbr-finder-prev]');
    var next = controls.querySelector('[data-bbr-finder-next]');
    var canNavigate = !swiper.isLocked && steps > 1;

    controls.hidden = !canNavigate;
    slider.toggleAttribute('data-bbr-finder-scrollable', canNavigate);
    if (dots && dots.childElementCount !== steps) {
      dots.replaceChildren();
      for (var i = 0; i < steps; i += 1) {
        var dot = document.createElement('span');
        dot.className = 'bbr-finder-dot';
        dots.appendChild(dot);
      }
    }
    if (dots) {
      Array.prototype.forEach.call(dots.children, function (dot, dotIndex) {
        dot.classList.toggle('is-active', dotIndex === index);
      });
    }
    if (prev) prev.disabled = !!swiper.isBeginning;
    if (next) next.disabled = !!swiper.isEnd;
  }

  function bindFinderSlider(slider) {
    if (!slider || slider.dataset.bbrFinderControls === 'true') return;
    if (!slider.querySelector('.bbr-finder-controls')) {
      slider.appendChild(createControls());
    }

    slider.dataset.bbrFinderControls = 'true';
    slider._bbrFinderTimeouts = [];
    slider._bbrFinderSwiperBound = false;
    slider._bbrFinderClickHandler = function (event) {
      var swiper = slider.swiper;
      if (!swiper) return;
      if (event.target.closest('[data-bbr-finder-prev]')) {
        event.preventDefault();
        swiper.slidePrev();
      }
      if (event.target.closest('[data-bbr-finder-next]')) {
        event.preventDefault();
        swiper.slideNext();
      }
      var updateTimer = setTimeout(function () {
        slider._bbrFinderTimeouts = (slider._bbrFinderTimeouts || []).filter(function (id) {
          return id !== updateTimer;
        });
        if (slider.isConnected) updateFinderControls(slider);
      }, 80);
      slider._bbrFinderTimeouts.push(updateTimer);
    };
    slider.addEventListener('click', slider._bbrFinderClickHandler);

    slider._bbrFinderUpdateHandler = function () {
      if (slider.isConnected) updateFinderControls(slider);
    };
    slider._bbrFinderReadyHandler = function () {
      if (slider._bbrFinderAttach) slider._bbrFinderAttach();
    };
    slider.addEventListener('slide-section:ready', slider._bbrFinderReadyHandler);
    slider._bbrFinderAttach = function () {
      if (!slider.isConnected) return false;
      if (!slider.swiper) return false;
      if (!slider._bbrFinderSwiperBound) {
        slider.swiper.on('slideChange resize update progress', slider._bbrFinderUpdateHandler);
        slider._bbrFinderSwiperBound = true;
      }
      updateFinderControls(slider);
      return true;
    };

    if ('IntersectionObserver' in window) {
      slider._bbrFinderVisibilityObserver = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        if (slider._bbrFinderAttach()) {
          slider._bbrFinderVisibilityObserver.disconnect();
          slider._bbrFinderVisibilityObserver = null;
          return;
        }
        var visibleAttachTimer = setTimeout(function () {
          slider._bbrFinderTimeouts = (slider._bbrFinderTimeouts || []).filter(function (id) {
            return id !== visibleAttachTimer;
          });
          if (slider._bbrFinderAttach && slider._bbrFinderAttach() && slider._bbrFinderVisibilityObserver) {
            slider._bbrFinderVisibilityObserver.disconnect();
            slider._bbrFinderVisibilityObserver = null;
          }
        }, 300);
        slider._bbrFinderTimeouts.push(visibleAttachTimer);
      }, { rootMargin: '200px 0px', threshold: 0.01 });
      slider._bbrFinderVisibilityObserver.observe(slider);
    }

    if (!slider._bbrFinderAttach()) {
      [300, 1000].forEach(function (delay) {
        var attachTimer = setTimeout(function () {
          slider._bbrFinderTimeouts = (slider._bbrFinderTimeouts || []).filter(function (id) {
            return id !== attachTimer;
          });
          if (slider._bbrFinderAttach) slider._bbrFinderAttach();
        }, delay);
        slider._bbrFinderTimeouts.push(attachTimer);
      });
    }
  }

  function cleanupFinderSlider(slider) {
    if (!slider || slider.dataset.bbrFinderControls !== 'true') return;
    if (slider._bbrFinderClickHandler) {
      slider.removeEventListener('click', slider._bbrFinderClickHandler);
    }
    if (slider._bbrFinderReadyHandler) {
      slider.removeEventListener('slide-section:ready', slider._bbrFinderReadyHandler);
    }
    if (slider.swiper && slider._bbrFinderUpdateHandler && slider._bbrFinderSwiperBound) {
      slider.swiper.off('slideChange resize update progress', slider._bbrFinderUpdateHandler);
    }
    if (slider._bbrFinderVisibilityObserver) {
      slider._bbrFinderVisibilityObserver.disconnect();
    }
    (slider._bbrFinderTimeouts || []).forEach(function (timeoutId) {
      clearTimeout(timeoutId);
    });
    delete slider._bbrFinderTimeouts;
    delete slider._bbrFinderClickHandler;
    delete slider._bbrFinderUpdateHandler;
    delete slider._bbrFinderReadyHandler;
    delete slider._bbrFinderAttach;
    delete slider._bbrFinderSwiperBound;
    delete slider._bbrFinderVisibilityObserver;
    delete slider.dataset.bbrFinderControls;
  }

  function initFinderControls(root) {
    (root || document).querySelectorAll('.section-suitable-finder .bbr-custom-products-slider').forEach(bindFinderSlider);
  }

  function cleanupFinderControls(root) {
    (root || document).querySelectorAll('.section-suitable-finder .bbr-custom-products-slider').forEach(cleanupFinderSlider);
  }

  function clearPendingFinderInit() {
    if (!finderInitTimeout) return;
    clearTimeout(finderInitTimeout);
    finderInitTimeout = null;
  }

  document.addEventListener('DOMContentLoaded', function () {
    initFinderControls(document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    initFinderControls(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    cleanupFinderControls(event.target);
  });
  window.addEventListener('pagehide', clearPendingFinderInit, { once: true });
  document.addEventListener('click', function (event) {
    if (!event.target.closest('.section-suitable-finder .product-tabs__header-item-js')) return;
    clearPendingFinderInit();
    finderInitTimeout = setTimeout(function () {
      finderInitTimeout = null;
      initFinderControls(document);
    }, 120);
  });
})();
